// Khorezmai — mobile-first control surface (Phase 2).
// Standalone /m route: queue + add task + live engine log + results + approve,
// driven from a phone. Self-contained — own socket, own state, reuses useAuth.
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { io } from 'socket.io-client';
import Conductor from './ConductorPanel';
import { OwnerApprovalCard } from './ClientApproval';
import RunVideo from './RunVideo';   // 🎥 etap videosi (docs/knowledge/198)
import OperatorChat from './OperatorChat';
import { attachConfirmBlink } from './confirmBlink';   // 🔔 tasdiq kutilsa yorliq sarlavhasi miltillaydi
import { useEmuAudio, EmuAudioBtn } from './emuAudio';   // 🔊 emulator ovozi (backend 204)   // 🧠 Loyiha agenti (docs/knowledge/190) — telefonda to'liq ekran
import { Toaster, toast } from 'react-hot-toast';
import { useAuth } from './AuthContext';
import { handleAuthExpired, handleMfaSetup } from './authExpired';
import { enablePush, sendTestPush, pushPermissionState, pushSupported } from './push';

const API = process.env.REACT_APP_API_URL ?? '';

const STATUS = {
  pending_architecture: { label: '🏗 Rejalashtirilmoqda', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  pending:              { label: '⏳ Navbatda',           cls: 'bg-gray-500/15 text-gray-300 border-gray-500/30' },
  in_progress:          { label: '🔄 Bajarilmoqda',       cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  completed:            { label: '✅ Tayyor',             cls: 'bg-green-500/15 text-green-300 border-green-500/30' },
  failed:               { label: '❌ Xato',               cls: 'bg-red-500/15 text-red-300 border-red-500/30' },
  cancelled:            { label: "⏹ To'xtatilgan",        cls: 'bg-gray-500/15 text-gray-400 border-gray-500/30' },
  awaiting_clarification:{ label: '❓ Aniqlik kerak',       cls: 'bg-amber-500/20 text-amber-200 border-amber-500/40' },
  awaiting_plan_approval:{ label: '📝 Reja tasdiq kutmoqda', cls: 'bg-indigo-500/20 text-indigo-200 border-indigo-500/40' },
  awaiting_owner_approval:{ label: '🙋 Ruxsat kutmoqda',    cls: 'bg-violet-500/20 text-violet-200 border-violet-500/40' },
};
// Subtask holati uchun ixcham belgi (navbat kartasidagi chiplar uchun)
const SUB_DOT = { pending_architecture: '🧠', pending: '⏳', in_progress: '🔄', completed: '✅', failed: '❌', cancelled: '⏹', awaiting_clarification: '❓', awaiting_plan_approval: '📝', awaiting_owner_approval: '🙋' };
const DEPLOY_M = {
  merge: '🔀 Merge', deploy: '🚀 Deploy', apk_build: '🔨 APK qurilmoqda',
  apk_install: '📲 O‘rnatilmoqda', installed: '📲 O‘rnatildi', prod: '🚀 Production',
  partial: '⚠️ Qisman', failed: '❌ Deploy xato', interrupted: '⚠️ Uzildi', done: '',
};
const JOB_ST = { running: ['🔄 Bajarilmoqda', 'bg-blue-500/20 text-blue-300'], done: ['✅ Tugadi', 'bg-emerald-500/20 text-emerald-300'],
  error: ['❌ Xato', 'bg-red-500/20 text-red-300'], cancelled: ['⏹ To‘xtatilgan', 'bg-gray-600/40 text-gray-300'],
  cancelling: ['⏹ To‘xtatilmoqda', 'bg-orange-500/20 text-orange-300'] };
const EMU_ST = { running: '● Ishlayapti', starting: '● Ishga tushmoqda…', reconnecting: '● Qayta ulanmoqda…', stopping: '● To‘xtatilmoqda', stopped: '● To‘xtatilgan', error: '● Xatolik', not_created: '● Yaratilmagan' };
function StatusChip({ status, approved, answered }) {
  // 💬 Savol-vazifa (Q&A) javob bilan yakunlangan — «Bajarildi» emas, «Javob tayyor».
  if (answered && status === 'completed') return <span className="text-xs px-2 py-0.5 rounded-full border bg-sky-500/15 text-sky-200 border-sky-500/40 whitespace-nowrap">💬 Javob tayyor</span>;
  if (approved) return <span className="text-xs px-2 py-0.5 rounded-full border bg-primary-600/15 text-primary-300 border-primary-600/30 whitespace-nowrap">🚀 Deploy qilindi</span>;
  const s = STATUS[status] || STATUS.pending;
  return <span className={`text-xs px-2 py-0.5 rounded-full border whitespace-nowrap ${s.cls}`}>{s.label}</span>;
}

function parseUrls(v) {
  if (!v) return [];
  try {
    const p = typeof v === 'string' ? JSON.parse(v) : v;
    if (Array.isArray(p)) return p.filter(Boolean);
    if (typeof p === 'string') return [p];
  } catch { if (typeof v === 'string' && v.startsWith('/')) return [v]; }
  return [];
}
const mediaUrl = (u) => (u && u.startsWith('http') ? u : `${API}${u}`);

// 📝 Reja tasdig'i (telefon): engine kod yozishdan oldin tuzgan «qaysi fayl / nima /
// nega» rejasi — push xabardan ochib shu yerda ✅ tasdiqlanadi yoki ✏️ qaytariladi.
function MobilePlanCard({ task, authFetch, onChanged }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [fb, setFb] = useState('');
  const [revise, setRevise] = useState(false);
  const round = task.plan_round || 0;
  useEffect(() => {
    let alive = true;
    authFetch(`${API}/tasks/${task.id}/plan`)
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => { if (alive) { if (ok) setData(d); else setErr(d.error || 'Reja yuklanmadi'); } })
      .catch(() => { if (alive) setErr('Reja yuklanmadi'); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.id, round]);
  const post = async (kind, body) => {
    setBusy(kind);
    try {
      const r = await authFetch(`${API}/tasks/${task.id}/plan/${kind}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        // ⚠️ Reja tuzilmagan (#2759) — server force'siz tasdiqlamaydi.
        if (kind === 'approve' && d.needsForce && !(body && body.force)) {
          setBusy('');
          if (confirmNoPlan(d.failReason)) return post('approve', { force: true });
          return;
        }
        throw new Error(d.error || 'xato');
      }
      toast.success(kind === 'approve' ? (body && body.force ? '⚠️ Rejasiz tasdiqlandi' : '✅ Reja tasdiqlandi') : (body && body.feedback ? '✏️ Reja qayta tuziladi' : '🔁 Reja qayta tuzilmoqda'));
      setFb(''); setRevise(false);
      setTimeout(onChanged, 800);
    } catch (e) { toast.error('Xato: ' + e.message); }
    finally { setBusy(''); }
  };
  const confirmNoPlan = (reason) => window.confirm(
    `Batafsil reja TUZILMAGAN${reason ? ` (${String(reason).replace(/^Batafsil reja tuzilmadi:\s*/i, '')})` : ''}.\n\n`
    + "Dasturchi fayl darajasidagi rejasiz, faqat qism vazifa matni bilan ishlaydi. Baribir REJASIZ tasdiqlaysizmi?");
  const plan = data && data.plan;
  const failed = !!(plan && plan.failed);
  const modName = (id) => { const m = ((data && data.modules) || []).find(x => String(x.id) === String(id)); return m ? m.name : `Modul #${id}`; };
  const ACT = { modify: "✏️", create: '➕', delete: '🗑' };
  const awaiting = task.status === 'awaiting_plan_approval';
  const Lst = ({ t, items }) => (items && items.length) ? (
    <div className="mt-2"><div className="text-[11px] font-semibold text-gray-400">{t}</div>
      <ul className="list-disc ml-5 text-xs text-gray-200 space-y-0.5">{items.map((x, i) => <li key={i} translate="no">{x}</li>)}</ul></div>
  ) : null;
  return (
    <div className="rounded-lg border border-indigo-500/40 bg-indigo-500/10 p-3">
      <div className="text-sm font-semibold text-indigo-200 mb-1">
        {awaiting && failed ? '⚠️ Reja TUZILMADI — qayta tuzdiring' : awaiting ? "📝 O'zgarish rejasi — tasdiqlang" : failed ? '⚠️ Rejasiz tasdiqlangan' : '📝 Tasdiqlangan reja'}{round > 1 ? ` · ${round}-variant` : ''}
      </div>
      {err && <div className="text-xs text-red-300">{err}</div>}
      {!err && !data && <div className="text-xs text-gray-400">Yuklanmoqda…</div>}
      {plan && (
        <div>
          {plan.failed && (
            <div className="text-xs text-amber-100 bg-amber-500/20 border border-amber-500/50 rounded-lg px-2.5 py-2 mb-2">
              <div className="font-semibold">⚠️ {plan.reason || 'Batafsil reja tuzilmadi'}</div>
              <div className="mt-0.5">Fayl darajasidagi reja YO'Q — quyida faqat qism vazifa matni.{awaiting ? ' Avval «🔁 Rejani qayta tuzish».' : ''}</div>
            </div>
          )}
          {plan.summary && <div className="text-sm text-gray-100 whitespace-pre-wrap" translate="no">{plan.summary}</div>}
          <Lst t="👤 Foydalanuvchi nimani ko'radi" items={plan.userVisible} />
          {(plan.modules || []).map((m, i) => (
            <div key={i} className="mt-2 rounded-lg bg-black/30 p-2">
              <div className="text-xs font-semibold text-gray-200 mb-1">{modName(m.module_id)}</div>
              {(m.changes || []).map((c, j) => (
                <div key={j} className="text-xs text-gray-300 mb-1.5" translate="no">
                  <div><span className="mr-1">{ACT[c.action] || '✏️'}</span><code className="font-mono text-[11px] text-indigo-200 break-all">{c.file}</code></div>
                  {c.what && <div>{c.what}</div>}
                  {c.why && <div className="text-gray-400">Nega: {c.why}</div>}
                </div>
              ))}
              {m.db && <div className="text-xs text-gray-300" translate="no">🗄 DB: {m.db}</div>}
              {m.api && <div className="text-xs text-gray-300" translate="no">🔌 API: {m.api}</div>}
              {m.note && <div className="text-xs text-gray-300 whitespace-pre-wrap" translate="no">{m.note}</div>}
            </div>
          ))}
          <Lst t="⚠️ Xavflar" items={plan.risks} />
          <Lst t="🧪 Qanday tekshiriladi" items={plan.verify} />
          <Lst t="🔒 Tegilmaydi" items={plan.notTouched} />
          <Lst t="❓ Hal qilinmagan savollar (izohda javob bering)" items={plan.openQuestions} />
        </div>
      )}
      {awaiting && (
        <div className="mt-3 space-y-2">
          {failed ? (<>
            <button onClick={() => post('revise', {})} disabled={!!busy}
                    className="w-full px-4 py-3 rounded-lg bg-primary-700 disabled:opacity-50 font-semibold text-base">
              {busy === 'revise' ? 'Yuborilmoqda…' : '🔁 Rejani qayta tuzish'}</button>
            <button onClick={() => { if (confirmNoPlan(plan.reason)) post('approve', { force: true }); }} disabled={!!busy}
                    className="w-full px-4 py-2.5 rounded-lg bg-gray-800 border border-amber-500/60 text-amber-200 disabled:opacity-50 font-semibold text-sm">
              {busy === 'approve' ? 'Tasdiqlanmoqda…' : '⚠️ Rejasiz tasdiqlash'}</button>
          </>) : (
            <button onClick={() => post('approve')} disabled={!!busy || !plan}
                    className="w-full px-4 py-3 rounded-lg bg-green-600 disabled:opacity-50 font-semibold text-base">
              {busy === 'approve' ? 'Tasdiqlanmoqda…' : '✅ Rejani tasdiqlash'}</button>
          )}
          <button onClick={() => setRevise(v => !v)} disabled={!!busy}
                  className="w-full px-4 py-2.5 rounded-lg bg-gray-800 border border-gray-600 font-semibold text-sm">✏️ Izoh bilan qaytarish</button>
          {revise && (
            <div>
              <textarea value={fb} onChange={e => setFb(e.target.value)} rows={3} placeholder="Nimani o'zgartirish kerak?"
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm" />
              <button onClick={() => post('revise', { feedback: fb })} disabled={!!busy || !fb.trim()}
                      className="w-full mt-1 px-4 py-2.5 rounded-lg bg-primary-700 disabled:opacity-50 font-semibold text-sm">
                {busy === 'revise' ? 'Yuborilmoqda…' : '📨 Rejani qayta tuzish'}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MobilePanel() {
  const { token, user } = useAuth();
  const authFetch = useCallback(async (url, opts = {}) => {
    const res = await fetch(url, { ...opts, headers: { Authorization: `Bearer ${token}`, ...(opts.headers || {}) } });
    if (res.status === 401) { handleAuthExpired(); throw new Error('unauthorized'); }
    // 🔐 2FA majburiy, hali sozlanmagan → sozlash sahifasi (docs/knowledge/148)
    if (res.status === 403) {
      const b = await res.clone().json().catch(() => null);
      if (b && b.code === 'MFA_SETUP_REQUIRED') { handleMfaSetup(); throw new Error('mfa_setup_required'); }
    }
    return res;
  }, [token]);

  const [projects, setProjects] = useState([]);
  // 🤝 Faqat mijoz bo'lgan hisob (backend docs/knowledge/207): agent, APK, emulator, Nazoratchi, tasdiq/boshqaruv, jonli log yo'q —
  // vazifa so'raydi, holat/natija, crash va topilmalarni ko'radi, savolga javob beradi.
  const isClient = projects.length > 0 && projects.every(p => p.my_role === 'client');
  const [projectId, setProjectId] = useState(() => localStorage.getItem('kh_m_project') || '');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  // ⚡ Tez yuklash (2026-10-30, operator: «task yuklash ko'p vaqt olayapti, loyihani almashtirsam yoki birinchi ochsam qotib
  // qolgandek»): ilgari har safar BARCHA loyihalarning 50 ta vazifasi to'liq matni bilan (~0.8 MB) yuklanib, tanlangani
  // brauzerda ajratilardi. Endi faqat tanlangan loyiha, 15 tadan, qisqartirilgan matn (slim=1, ~30–60 KB); ro'yxat loyiha
  // bo'yicha xotirada — loyihaga qaytilganda bir zumda ko'rinadi, yangilash fonda. To'liq matn — vazifa ochilganda.
  const PAGE = 15;
  const tasksCacheRef = useRef(new Map());            // loyiha kaliti ('all' | id) → { list, more }
  const projKeyRef = useRef(projectId || 'all'); projKeyRef.current = projectId || 'all';
  const [tasksMore, setTasksMore] = useState(false);
  const [tasksBusy, setTasksBusy] = useState(false);  // fonda yangilanmoqda (ro'yxat ko'rinib turadi)
  const [moreBusy, setMoreBusy] = useState(false);
  const [openTaskFull, setOpenTaskFull] = useState(null);     // ochilgan vazifaning to'liq ma'lumoti (/tasks/:id?full=1)
  const [newTask, setNewTask] = useState('');
  // 📝 Reja tasdig'i — tanlov eslab qolinadi (ish stolidagi chip bilan bir xil kalit).
  const [planReview, setPlanReviewRaw] = useState(() => { try { return localStorage.getItem('kh_plan_review') === '1'; } catch { return false; } });
  const setPlanReview = (v) => { setPlanReviewRaw(v); try { localStorage.setItem('kh_plan_review', v ? '1' : '0'); } catch {} };
  const [adding, setAdding] = useState(false);
  const [apkBusy, setApkBusy] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [logBuffer, setLogBuffer] = useState([]); // {taskId, output, isError}
  const [approvingId, setApprovingId] = useState(null);
  const [pushState, setPushState] = useState(pushPermissionState());
  const [listening, setListening] = useState(false);
  const [openSubs, setOpenSubs] = useState({}); // subtask id -> to'liq matn ochiqmi (detal)
  const toggleSub = (id) => setOpenSubs(s => ({ ...s, [id]: !s[id] }));
  const logEndRef = useRef(null);
  const refetchTimer = useRef(null);
  const socketRef = useRef(null);
  // 🔊 Emulator ovozi — to'liq ekran emulator tepasidagi tugma; ✕ bilan yopilsa ovoz ham o'chadi.
  const { audio: emuAudio, toggleAudio: toggleEmuAudio } = useEmuAudio(socketRef, { notify: (m) => toast(m, { id: 'kh-emu-audio', duration: 6000 }) });

  // ---- emulator (multi-slot grid, fullscreen on tap) ----
  const [emuOpen, setEmuOpen] = useState(false);
  const [emuList, setEmuList] = useState([]);
  const [emuMaxSlots, setEmuMaxSlots] = useState(3);
  const [emuFrames, setEmuFrames] = useState({});        // slot -> src-ready URL (data: yoki blob:)
  const emuFrameUrlRef = useRef({});                      // slot -> oxirgi blob URL (revoke uchun)
  const [emuStreaming, setEmuStreaming] = useState({});
  const emuStreamingRef = useRef({});
  useEffect(() => { emuStreamingRef.current = emuStreaming; }, [emuStreaming]);
  const emuOpenRef = useRef(false);
  useEffect(() => { emuOpenRef.current = emuOpen; }, [emuOpen]);
  // 📱 iPhone qotishi (2026-10-28, operator: «telefonimda hech narsasi bosilmayapti»): har log qatori butun panelni qayta
  // chizardi — engine + agent sinovi bir paytda sekundiga o'nlab qator yuborganda iPhone asosiy oqimi band bo'lib, bosishlar
  // yetib bormasdi. Endi qatorlar ref'da yig'iladi va 1 s da bir marta holatga quyiladi.
  const logPendingRef = useRef([]);
  const logTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(logTimerRef.current), []);
  const [emuStatuses, setEmuStatuses] = useState({});
  const [emuBusy, setEmuBusy] = useState({});
  const [emuFull, setEmuFull] = useState(null); // slot in fullscreen
  const emuDragRef = useRef({});

  // ---- data ----
  const fetchProjects = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/projects`);
      const data = await r.json();
      if (Array.isArray(data)) {
        setProjects(data);
        // Eslab qolingan loyiha endi ro'yxatda bo'lmasa (a'zolik olib tashlangan yoki
        // ilgari izolyatsiya yo'qligida BOSHQA userning loyihasi tanlangan) — birinchisiga o'tamiz.
        // Bitta loyiha bo'lsa — doim o'sha (eslab qolingan «Hammasi» ham emas).
        setProjectId(prev => (data.length === 1 ? String(data[0].id)
          : (prev && (prev === 'all' || data.some(p => String(p.id) === String(prev)))) ? prev : (data[0] ? String(data[0].id) : '')));
      }
    } catch {}
  }, [authFetch]);

  const fetchTasks = useCallback(async ({ more = false } = {}) => {
    const key = projKeyRef.current;
    const cached = tasksCacheRef.current.get(key);
    const have = cached ? cached.list : [];
    // Yangilashda ochib qo'yilgan sahifalar ham qayta olinadi (≤ 60 ta); «yana» — keyingi 15 ta.
    const limit = more ? PAGE : Math.min(Math.max(PAGE, have.length), 60);
    const offset = more ? have.length : 0;
    if (more) setMoreBusy(true); else setTasksBusy(true);
    try {
      const r = await authFetch(`${API}/tasks?slim=1&limit=${limit}&offset=${offset}${key !== 'all' ? `&project_id=${encodeURIComponent(key)}` : ''}`);
      const data = await r.json();
      if (!Array.isArray(data)) return;
      const list = more ? [...have, ...data.filter(t => !have.some(h => h.id === t.id))] : data;
      const entry = { list, more: data.length >= limit };
      tasksCacheRef.current.set(key, entry);
      if (projKeyRef.current === key) { setTasks(list); setTasksMore(entry.more); setLoading(false); }
    } catch {} finally { if (more) setMoreBusy(false); else setTasksBusy(false); }
  }, [authFetch]);

  // Qo'shimcha ma'lumotlar. Har biri ALOHIDA try — bittasi ruxsat bermasa
  // (masalan salomatlik faqat admin uchun) qolganlari baribir yuklanadi.
  const fetchExtras = useCallback(async () => {
    const get = async (url) => {
      try { const r = await authFetch(`${API}${url}`); if (!r.ok) return null; return await r.json(); }
      catch { return null; }
    };
    // ⚡ Parallel (ilgari ketma-ket — telefonda 4 ta so'rov kechikishi qo'shilib, loyiha almashganda bo'limlar sekin yangilanardi).
    const pq0 = projectId && projectId !== 'all' ? `?projectId=${projectId}` : '';
    const [j, c, f, h] = await Promise.all([get('/jobs/active'), get('/crash-reports' + pq0), get('/agent-findings' + pq0), get('/health-checks')]);
    if (Array.isArray(j)) setJobs(j);
    // 🔴 Loyiha tanlangan bo'lsa FAQAT o'sha loyihaniki: ilgari projectId'siz so'ralib
    // a'zo bo'lgan barcha loyihalar topilma/crashlari aralash chiqardi (operator:
    // "faqat viloyatga tegishli topilmalar chiqmagan" — 2026-09-14). Ish stolidagi
    // 🐞 panel bilan bir xil qoida.
    if (c && Array.isArray(c.crashes)) setCrashes(c.crashes);
    if (f && Array.isArray(f.findings)) setFindings(f.findings);
    if (h && Array.isArray(h.checks)) setHealth(h.checks);
  }, [authFetch, projectId]);

  // 🔁 Zaxira so'rov: socket voqeasi yo'qolsa ham holat yangilanadi (iOS PWA fonda socketni uzadi).
  // Faol vazifa bo'lsa 10s, bo'lmasa 60s; ekran ko'rinmasa so'ramaymiz.
  const fetchTasksRef = useRef(null);
  fetchTasksRef.current = fetchTasks;
  const hasActiveRef = useRef(false);
  useEffect(() => {
    let last = 0;
    const t = setInterval(() => {
      if (document.hidden) return;
      const every = hasActiveRef.current ? 10000 : 60000;
      if (Date.now() - last < every) return;
      last = Date.now();
      fetchTasksRef.current && fetchTasksRef.current();
    }, 5000);
    return () => clearInterval(t);
  }, []);

  // Throttle (≤ 1 marta / 4 s): engine/sinov holat voqealari sekundiga kelganda har safar ~0.7 MB ro'yxat qayta yuklanib
  // chizilardi — iPhone'da panel qotib qolardi (2026-10-28). Birinchi voqea 0.7 s da, keyingilari oldingisidan ≥ 4 s keyin.
  const lastRefetchRef = useRef(0);
  const scheduleRefetch = useCallback(() => {
    if (refetchTimer.current) return;
    const wait = Math.max(700, 4000 - (Date.now() - lastRefetchRef.current));
    refetchTimer.current = setTimeout(() => { refetchTimer.current = null; lastRefetchRef.current = Date.now(); fetchTasks(); }, wait);
  }, [fetchTasks]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);
  // Loyiha almashdi (yoki birinchi ochilish): xotiradagi ro'yxat DARHOL, yangisi fonda; xotirada yo'q bo'lsa «Yuklanmoqda…».
  useEffect(() => {
    const c = tasksCacheRef.current.get(projectId || 'all');
    setTasks(c ? c.list : []); setTasksMore(c ? c.more : false); setLoading(!c);
    fetchTasks();
  }, [projectId, fetchTasks]);
  useEffect(() => {
    fetchExtras();
    const t = setInterval(() => { if (!document.hidden) fetchExtras(); }, 20000);
    return () => clearInterval(t);
  }, [fetchExtras]);

  // deep link: /m?task=N
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get('task');
    if (q) setOpenId(Number(q));
  }, []);

  useEffect(() => { if (projectId) localStorage.setItem('kh_m_project', projectId); }, [projectId]);

  // ---- socket ----
  useEffect(() => {
    const socket = io(API, { auth: { token } });
    socketRef.current = socket;
    attachConfirmBlink(socket);   // 🔔 kutilayotgan tasdiq — sarlavha miltillaydi
    socket.on('new_log', (data) => {
      if (data && (data.output || data.status)) {
        if (data.output) {
          logPendingRef.current.push({ taskId: data.taskId, output: data.output, isError: data.isError });
          if (!logTimerRef.current) {
            logTimerRef.current = setTimeout(() => {
              logTimerRef.current = null;
              const batch = logPendingRef.current; logPendingRef.current = [];
              if (batch.length) setLogBuffer(prev => [...prev, ...batch].slice(-400));
            }, 1000);
          }
        }
        if (data.status) scheduleRefetch();
      }
    });
    // Emulator streaming (multi-slot): frames carry {slot, data, fmt?}.
    // data = base64 STRING (eski backend, PNG) YOKI BINARY ArrayBuffer (yangi: JPEG/PNG
    // Buffer). Ikkalasi ham src-ready URL'ga aylantiriladi; blob URL'lar leak bo'lmasligi
    // uchun eskisi kechiktirib revoke qilinadi.
    socket.on('emulator:frame', (payload) => {
      const slot = (payload && typeof payload === 'object') ? (payload.slot || 0) : 0;
      const data = (payload && typeof payload === 'object') ? payload.data : payload;
      let src;
      if (typeof data === 'string') {
        src = `data:image/png;base64,${data}`;
      } else if (data) {
        try {
          const mime = payload && payload.fmt === 'jpg' ? 'image/jpeg' : 'image/png';
          src = URL.createObjectURL(new Blob([data], { type: mime }));
        } catch { return; }
        const old = emuFrameUrlRef.current[slot];
        emuFrameUrlRef.current[slot] = src;
        if (old && old.startsWith('blob:')) setTimeout(() => { try { URL.revokeObjectURL(old); } catch {} }, 2000);
      } else return;
      setEmuFrames(f => ({ ...f, [slot]: src }));
    });
    // AUTO-LOCK: server harakatsizlikdan oqimni to'xtatdi — holatni to'xtagan qilib qo'yamiz,
    // katakda ▶ Boshlash qaytadi (davom etish = o'sha tugma yoki ⛶).
    socket.on('emulator:stream_idle_stop', (payload) => {
      const slot = (payload && payload.slot) || 0;
      setEmuStreaming(s => ({ ...s, [slot]: false }));
    });
    // 🔒 Administrator emulatorni boshqaruvga olgan — server bosishni rad etdi; sababini aytamiz (docs/knowledge/184).
    socket.on('emulator:locked', (data) => {
      const myId = JSON.parse(localStorage.getItem('khorezmai_user') || '{}')?.id;
      if (data && myId && data.userId && String(data.userId) !== String(myId)) return;
      toast(`🔒 Administrator (${(data && data.by) || 'admin'}) boshqaryapti — hozircha faqat ko'rasiz`, { id: 'emu-admin-lock' });
    });
    socket.on('emulator:status', (data) => {
      const myId = JSON.parse(localStorage.getItem('khorezmai_user') || '{}')?.id;
      if (myId && data.userId && String(data.userId) !== String(myId)) return;
      const slot = data.slot || 0;
      setEmuStatuses(s => ({ ...s, [slot]: data.status }));
      setEmuBusy(b => ({ ...b, [slot]: false }));
      if (data.status === 'running' && emuOpenRef.current) setTimeout(() => { socket.emit('emulator:start_stream', { slot }); setEmuStreaming(st => ({ ...st, [slot]: true })); }, 800);
    });
    socket.on('connect', () => {
      // 🔁 Qayta ulanish (telefon uxlab qolgan / PWA fonda edi) — o'tkazib yuborilgan
      // «tugadi» voqeasini yo'qotmaslik uchun ro'yxatni darhol qayta o'qiymiz.
      scheduleRefetch();
      const streaming = emuStreamingRef.current || {};
      Object.keys(streaming).forEach(slot => { if (streaming[slot]) socket.emit('emulator:start_stream', { slot: +slot }); });
    });
    return () => { socket.disconnect(); };
  }, [token, scheduleRefetch]);

  // VISIBILITY-PAUZA: PWA/tab fonga o'tganda emulator oqimini to'xtatadi, ko'ringanda tiklaydi
  // (fon WebSocket tirik qolib screencap loop'i serverni bosmasin — App.js'dagi bilan bir xil).
  useEffect(() => {
    const onVis = () => {
      const sock = socketRef.current;
      if (!sock) return;
      if (document.visibilityState === 'hidden') {
        sock.emit('emulator:stop_stream'); // slot yo'q → barcha oqim
      } else {
        fetchTasksRef.current && fetchTasksRef.current();   // ilovaga qaytdi — holat eskirgan bo'lishi mumkin
        const streaming = emuStreamingRef.current || {};
        Object.keys(streaming).forEach(slot => { if (streaming[slot]) sock.emit('emulator:start_stream', { slot: +slot }); });
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Ro'yxatdagi karta qisqartirilgan (slim) — ochilganda to'liq matn/media /tasks/:id?full=1 dan; holatlar ro'yxatdagidek yangi qoladi.
  // Boshqa loyihaning vazifasi (Nazoratchi/crash havolasi, ?task=N) ro'yxatda bo'lmasa ham ochiladi.
  useEffect(() => {
    if (!openId) { setOpenTaskFull(null); return undefined; }
    let alive = true;
    authFetch(`${API}/tasks/${openId}?full=1`).then(r => (r.ok ? r.json() : null)).then(d => { if (alive && d && d.id) setOpenTaskFull(d); }).catch(() => {});
    return () => { alive = false; };
  }, [openId, authFetch]);
  const openTask = useMemo(() => {
    const li = tasks.find(t => String(t.id) === String(openId)) || null;
    const full = openTaskFull && String(openTaskFull.id) === String(openId) ? openTaskFull : null;
    if (!full) return li;
    if (!li) return full;
    const fsub = new Map((full.sub_tasks || []).map(x => [x.id, x]));
    return { ...li, instruction: full.instruction,
      sub_tasks: (li.sub_tasks || []).map(x => { const fx = fsub.get(x.id); return fx ? { ...x, instruction: fx.instruction, screenshot_urls: fx.screenshot_urls ?? x.screenshot_urls, video_urls: fx.video_urls ?? x.video_urls } : x; }) };
  }, [tasks, openId, openTaskFull]);
  const ACTIVE_ST = ['pending', 'pending_architecture', 'in_progress', 'awaiting_clarification', 'awaiting_plan_approval', 'awaiting_owner_approval'];
  hasActiveRef.current = tasks.some(t => ACTIVE_ST.includes(t.status) || (t.sub_tasks || []).some(st => ACTIVE_ST.includes(st.status)));
  const relevantIds = useMemo(() => {
    if (!openTask) return new Set();
    const ids = new Set([String(openTask.id)]);
    (openTask.sub_tasks || []).forEach(st => ids.add(String(st.id)));
    return ids;
  }, [openTask]);
  const openLogs = useMemo(
    () => logBuffer.filter(l => relevantIds.has(String(l.taskId))),
    [logBuffer, relevantIds]
  );
  useEffect(() => { logEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [openLogs.length]);

  // ---- actions ----
  const addTask = async () => {
    const instruction = newTask.trim();
    if (!instruction) return;
    if (!projectId || projectId === 'all') { toast.error('Avval aniq loyiha tanlang'); return; }
    setAdding(true);
    try {
      const r = await authFetch(`${API}/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: Number(projectId), instruction, task_type: 'developer', plan_review: planReview }),
      });
      if (!r.ok) throw new Error('xato');
      setNewTask('');
      toast.success('Vazifa navbatga qo‘shildi');
      setTimeout(fetchTasks, 400);
    } catch { toast.error('Vazifa qo‘shilmadi'); }
    finally { setAdding(false); }
  };

  // 📱 Telefon versiyasi ish stolidan ancha orqada qolgan edi: fon jarayonlari
  // (logo/TZ/kalit tuzatish hisobotlari), crashlar, topilmalar, salomatlik va
  // vazifa boshqaruvi (davom/to'xtatish/tekshirish) faqat kompyuterda bor edi.
  const [jobs, setJobs] = useState([]);
  const [crashes, setCrashes] = useState([]);
  const [findings, setFindings] = useState([]);
  const [health, setHealth] = useState(null);      // null = ruxsat yo'q / yuklanmagan
  const [panel, setPanel] = useState('');          // ochiq bo'lim
  // 💥 Crashni TELEFONDAN tuzatish: stack (sabab), tuzatish izi (qayerda bor)
  // va bir bosishda vazifa. Operator: "kompyuterim bo'lmasa ham telefondan
  // crashlarni tuzata olay" — shuning uchun butun sikl shu yerda.
  // 🧑‍💼 NAZORATCHI — Khorezmai ustida turadigan avtonom QA-boshqaruvchi. Telefon
  // uning ASOSIY pulti: taklif keladi → ✅ Bajar → o'zi sinaydi → topilmani vazifa
  // qiladi → deploy'dan keyin qayta sinaydi → faqat savol bo'lsa shu yerga chiqadi.
  const [sv, setSv] = useState(null);                 // {state, items, running}
  const [svOpen, setSvOpen] = useState(() => !!new URLSearchParams(window.location.search).get('supervisor'));
  const [svBusy, setSvBusy] = useState('');
  const [svAnswer, setSvAnswer] = useState({});       // itemId -> matn
  const [svWish, setSvWish] = useState('');
  const [svVoice, setSvVoice] = useState(false);    // 🔊 ovozli sinov — faqat tanlanganda
  const [svRecord, setSvRecord] = useState(false);  // 🎥 etap videosi — faqat tanlanganda
  const [svExpanded, setSvExpanded] = useState({});   // itemId -> stsenariy ochiqmi
  const [svShowDone, setSvShowDone] = useState(false);
  const fetchSupervisor = useCallback(async () => {
    if (!projectId || projectId === 'all') { setSv(null); return; }
    try {
      const r = await authFetch(`${API}/supervisor?projectId=${projectId}`);
      if (!r.ok) { setSv(null); return; }
      const j = await r.json();
      if (j && Array.isArray(j.items)) setSv(j);
    } catch {}
  }, [authFetch, projectId]);
  useEffect(() => {
    fetchSupervisor();
    const t = setInterval(() => { if (!document.hidden) fetchSupervisor(); }, 15000);
    return () => clearInterval(t);
  }, [fetchSupervisor]);
  // deep link: /m?supervisor=<projectId> (push/Telegram'dan keladi)
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get('supervisor');
    if (q && /^\d+$/.test(q)) { setProjectId(q); setSvOpen(true); }
  }, []);
  // 🧠 Loyiha agenti (2026-11-01, operator: «agentni telefonga qo'ymabsan»): ilgari faqat desktop'da edi — telefonda desktop
  // versiyada «⏳ Jarayonlar» tugmasi navbar'dan chiqib ketardi. Endi /m da to'liq ekran. Push havolasi `/?agent=<loyiha>`
  // telefonda `/m?agent=<loyiha>` ga aylanadi (App.js) — shu yerda o'sha loyiha agenti ochiladi. Telefonning «orqaga»
  // ishorasi oynani yopadi (history), sahifadan chiqib ketmaydi.
  const [agentOpen, setAgentOpen] = useState(() => /^\d+$/.test(new URLSearchParams(window.location.search).get('agent') || ''));
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get('agent');
    if (q && /^\d+$/.test(q)) setProjectId(q);
  }, []);
  const openAgent = () => {
    try { window.history.pushState({ khAgent: 1 }, ''); } catch (e) { }
    setAgentOpen(true);
  };
  const closeAgent = () => {
    if (window.history.state && window.history.state.khAgent) window.history.back();   // popstate yopadi
    else setAgentOpen(false);
  };
  useEffect(() => {
    const onPop = () => setAgentOpen(!!(window.history.state && window.history.state.khAgent));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  useEffect(() => {
    if (!agentOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';   // ortdagi ro'yxat oyna ostida aylanmasin (iOS)
    return () => { document.body.style.overflow = prev; };
  }, [agentOpen]);
  const svAct = async (id, action, body = {}) => {
    setSvBusy(`${id}:${action}`);
    try {
      const r = await authFetch(`${API}/supervisor/items/${id}/${action}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'xato');
      toast.success(action === 'approve' ? 'Navbatga qo‘yildi — o‘zim sinayman' : action === 'skip' ? 'O‘tkazib yuborildi' : 'Qabul qilindi');
      setSvAnswer(a => ({ ...a, [id]: '' }));
      fetchSupervisor();
    } catch (e) { toast.error(e.message || 'Xato'); }
    finally { setSvBusy(''); }
  };
  const svSetState = async (patch) => {
    setSvBusy('state');
    try {
      const r = await authFetch(`${API}/supervisor/state`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ projectId: Number(projectId), ...patch }),
      });
      if (!r.ok) throw new Error('xato');
      toast.success(patch.enabled === false ? 'Nazoratchi o‘chirildi' : patch.enabled ? 'Nazoratchi yoqildi — birinchi skan ketdi' : 'Saqlandi');
      fetchSupervisor();
    } catch { toast.error('Saqlanmadi'); }
    finally { setSvBusy(''); }
  };
  const svScan = async () => {
    setSvBusy('scan');
    try {
      const r = await authFetch(`${API}/supervisor/scan`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ projectId: Number(projectId) }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'xato');
      toast.success(j.created ? `${j.created} ta yangi taklif` : j.reason === 'pending_proposals' ? 'Avval mavjud takliflarga javob bering' : 'Sinalmagan yangi ish topilmadi');
      fetchSupervisor();
    } catch (e) { toast.error(e.message || 'Xato'); }
    finally { setSvBusy(''); }
  };
  // 🎯 NISHON TANLOVI — 2026-09-14: telefondan «Mijoz buyurtma berishi va haydovchi …» deb
  // berilgan sinov BITTA emulatorga tushdi (standart devices=1, tanlagich yo'q edi).
  // Endi ish stolidagi 🤖 Agent sinovi qatoridagi chiplarning egizagi shu yerda:
  // «Shuni sina» uchun umumiy tanlov + har taklif kartasida o'z tanlovi.
  const [svPick, setSvPick] = useState(null);          // {slots:[...], web:id|null} — «Shuni sina»
  const [svItemPick, setSvItemPick] = useState({});     // itemId -> {slots, web}
  const svAvail = (sv && sv.targetsAvail) || null;
  useEffect(() => {
    // Standart: ishlab turgan emulatorlar; hech biri yoqilmagan bo'lsa — 1-emulator.
    if (!svAvail || svPick) return;
    const running = (svAvail.emulators || []).filter(e => e.status === 'running').map(e => e.slot);
    setSvPick({ slots: svAvail.mobile ? (running.length ? running : [0]) : [], web: (!svAvail.mobile && svAvail.webs && svAvail.webs[0]) ? svAvail.webs[0].id : null });
  }, [svAvail, svPick]);
  useEffect(() => { setSvPick(null); setSvItemPick({}); }, [projectId]);
  const pickFor = (it) => svItemPick[it.id] || {
    slots: Array.isArray(it.targets?.slots) && it.targets.slots.length ? it.targets.slots
         : (it.targets?.mobileModuleId ? (it.targets.devices >= 2 ? [0, 1] : [0]) : []),
    web: it.targets?.webModuleId || null,
  };
  const togglePick = (pick, setPick, kind, val) => {
    const p = { slots: [...(pick.slots || [])], web: pick.web || null };
    if (kind === 'slot') p.slots = p.slots.includes(val) ? p.slots.filter(x => x !== val) : [...p.slots, val].sort((a, b) => a - b);
    else p.web = p.web === val ? null : val;
    setPick(p);
  };
  // Chiplar: 📱 E1/E2/E3 (● yoqilgan, ○ o'chiq — tanlansa o'zi yoqiladi + ilova o'rnatiladi) + 🌐 saytlar
  const renderTargetChips = (pick, setPick, small = false) => {
    if (!svAvail || !pick) return null;
    const cls = (on) => `${small ? 'px-2 py-1 text-[10px]' : 'px-2.5 py-1.5 text-[11px]'} rounded-lg border ${on ? 'bg-primary-700 border-primary-600 text-white' : 'bg-gray-900 border-gray-700 text-gray-400'}`;
    const slots = [];
    if (svAvail.mobile) for (let i = 0; i < (svAvail.maxSlots || 3); i++) {
      const e = (svAvail.emulators || []).find(x => x.slot === i);
      const on = (pick.slots || []).includes(i);
      slots.push(
        <button key={'s' + i} type="button" onClick={() => togglePick(pick, setPick, 'slot', i)} className={cls(on)}
                title={e && e.status === 'running' ? 'Ishlab turibdi' : 'O‘chiq — tanlansa o‘zi yoqiladi va ilova o‘rnatiladi'}>
          {on ? '☑' : '☐'} 📱 E{i + 1} {e && e.status === 'running' ? '●' : '○'}
        </button>
      );
    }
    const webs = (svAvail.webs || []).map(w => {
      const on = pick.web === w.id;
      return <button key={'w' + w.id} type="button" onClick={() => togglePick(pick, setPick, 'web', w.id)} className={cls(on)}>{on ? '☑' : '☐'} 🌐 {w.name}</button>;
    });
    const n = (pick.slots || []).length + (pick.web ? 1 : 0);
    return (
      <div className="flex items-center gap-1.5 flex-wrap">
        {slots}{webs}
        <span className={`text-[10px] ${n ? 'text-gray-500' : 'text-red-400'}`}>{n ? (n > 1 ? `agent ${n} nishonni birga boshqaradi` : '') : 'kamida bitta nishon'}</span>
      </div>
    );
  };
  const svPropose = async () => {
    const scenario = svWish.trim(); if (scenario.length < 5) return;
    const pick = svPick || { slots: [], web: null };
    if (!(pick.slots || []).length && !pick.web) { toast.error('Kamida bitta nishon tanlang (emulator yoki sayt)'); return; }
    setSvBusy('propose');
    try {
      const r = await authFetch(`${API}/supervisor/propose`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: Number(projectId), scenario, slots: pick.slots || [], webModuleId: pick.web || null,
          ...(svVoice ? { narrate: true } : {}), ...(svRecord ? { record: true } : {}) }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'xato');
      const t = j.targets || {};
      const where = [...((t.slots || []).map(sl => `E${sl + 1}`)), t.webModuleId ? '🌐' : null].filter(Boolean).join(' + ');
      setSvWish(''); toast.success(`Navbatga qo‘yildi — ${where || 'nishon'} da o‘zim sinayman`);
      fetchSupervisor();
    } catch (e) { toast.error(e.message || 'Xato'); }
    finally { setSvBusy(''); }
  };
  const svApproveAll = async () => {
    setSvBusy('all');
    try {
      const r = await authFetch(`${API}/supervisor/approve-all`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ projectId: Number(projectId) }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'xato');
      toast.success(`${j.approved} ta sinov navbatda`);
      fetchSupervisor();
    } catch (e) { toast.error(e.message || 'Xato'); }
    finally { setSvBusy(''); }
  };
  const svStop = async () => {
    if (!window.confirm('Ketayotgan sinov to‘xtatilsinmi?')) return;
    try {
      const r = await authFetch(`${API}/agent-qa/stop`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ projectId: Number(projectId) }),
      });
      if (r.ok) toast.success('To‘xtatilmoqda…'); else toast.error('To‘xtatilmadi');
    } catch { toast.error('Xato'); }
  };
  const [crashOpen, setCrashOpen] = useState(null);   // ochiq crash id
  const onlyProject = (list) => (projectId && projectId !== 'all')
    ? list.filter(x => String(x.project_id) === String(projectId)) : list;
  const visibleCrashes = useMemo(() => onlyProject(crashes), [crashes, projectId]);   // eslint-disable-line react-hooks/exhaustive-deps
  // ✔ Tuzatilganlar (fix vazifasi tasdiqlangan, docs/knowledge/200) va rad etilganlar telefon ro'yxatida ko'rinmaydi.
  const visibleFindings = useMemo(() => onlyProject(findings).filter(f => f.status !== 'fixed' && f.status !== 'dismissed'), [findings, projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [crashInfo, setCrashInfo] = useState({});     // id -> {stack, ...}
  const [crashTrace, setCrashTrace] = useState({});   // id -> tuzatish izi
  const [crashBusy, setCrashBusy] = useState(null);
  const [actBusy, setActBusy] = useState(null);

  // Vazifa boshqaruvi — ish stolidagi ⏳ Jarayonlar bilan BIR XIL endpointlar.
  const taskAction = async (id, path, opts, okMsg) => {
    setActBusy(id + path);
    try {
      const r = await authFetch(`${API}/tasks/${id}${path}`, opts || { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.error || `Xato ${r.status}`); return null; }
      if (okMsg) toast.success(okMsg);
      setTimeout(fetchTasks, 500);
      return d;
    } catch { toast.error('Server javob bermadi'); return null; }
    finally { setActBusy(null); }
  };
  const jsonPost = (body) => ({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

  // Stack ro'yxat bilan KELMAYDI (og'ir) — ochilganda alohida so'raladi.
  const openCrash = async (c) => {
    const next = crashOpen === c.id ? null : c.id;
    setCrashOpen(next);
    if (next === null) return;
    if (!crashInfo[c.id]) {
      try {
        const r = await authFetch(`${API}/crash-reports/${c.id}`);
        if (r.ok) { const d = await r.json(); setCrashInfo(m => ({ ...m, [c.id]: d.crash || d })); }
      } catch {}
    }
    if (c.task_id && !crashTrace[c.id]) {
      try {
        const r = await authFetch(`${API}/crash-reports/${c.id}/fix`);
        if (r.ok) { const d = await r.json(); setCrashTrace(m => ({ ...m, [c.id]: d })); }
      } catch {}
    }
  };

  const crashFixTask = async (c) => {
    setCrashBusy(c.id);
    try {
      const r = await authFetch(`${API}/crash-reports/${c.id}/fix-task`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.error || 'Yaratilmadi'); return; }
      toast.success('Tuzatish vazifasi yaratildi');
      await fetchExtras(); await fetchTasks();
      if (d.taskId) { setPanel(''); setOpenId(d.taskId); }   // darhol vazifaga o'tamiz
    } catch { toast.error('Server javob bermadi'); }
    finally { setCrashBusy(null); }
  };

  const crashDelete = async (c) => {
    if (!window.confirm('Crash yozuvi o‘chirilsinmi?')) return;
    setCrashBusy(c.id);
    try {
      const r = await authFetch(`${API}/crash-reports/${c.id}`, { method: 'DELETE' });
      if (r.ok) { toast.success('O‘chirildi'); fetchExtras(); } else toast.error('O‘chirilmadi');
    } catch { toast.error('Server javob bermadi'); }
    finally { setCrashBusy(null); }
  };
  const jsonPut  = (body) => ({ method: 'PUT',  headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

  const [clarA, setClarA] = useState({}); // {taskId: {qIdx: answer}}
  const [clarBusy, setClarBusy] = useState(null);
  const parseQ = (t) => { try { const q = JSON.parse(t.clarification_questions || '[]'); return Array.isArray(q) ? q : []; } catch { return []; } };
  const sendClar = async (t) => {
    const qs = parseQ(t);
    const answers = qs.map((_, i) => (clarA[t.id]?.[i] || '').trim());
    if (answers.every(a => !a)) { toast.error('Kamida bitta javob kiriting'); return; }
    setClarBusy(t.id);
    try {
      const r = await authFetch(`${API}/tasks/${t.id}/clarify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ answers }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'xato');
      toast.success('✅ Javob yuborildi');
      setClarA(s => { const c = { ...s }; delete c[t.id]; return c; });
      setTimeout(fetchTasks, 1200);
    } catch (e) { toast.error('Xato: ' + (e.message || '')); }
    finally { setClarBusy(null); }
  };

  const approve = async (taskId) => {
    setApprovingId(taskId);
    toast('Merge + redeploy boshlandi…', { icon: '🚀' });
    try {
      const r = await authFetch(`${API}/tasks/${taskId}/approve`, { method: 'POST' });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.error) throw new Error(data.error || 'xato');
      // progress streams over new_log (taskId = parent); poll for approved_at
      setTimeout(fetchTasks, 1500);
    } catch (e) { toast.error('Approve xato: ' + (e.message || '')); setApprovingId(null); }
  };
  // clear the approving spinner once the server marks it approved
  useEffect(() => {
    if (approvingId && openTask && String(openTask.id) === String(approvingId) && openTask.approved_at) {
      setApprovingId(null);
      toast.success('Deploy qilindi 🚀');
    }
  }, [tasks, approvingId, openTask]);

  // Download the built APK to THIS phone (native browser download → tap to install).
  const downloadApk = async () => {
    if (!projectId || projectId === 'all') { toast.error('Avval aniq loyiha tanlang'); return; }
    setApkBusy(true);
    try {
      const r = await authFetch(`${API}/projects/${projectId}/mobile/apk-link`);
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.error) { toast.error(data.error || 'APK topilmadi'); return; }
      const mb = (data.sizeBytes / 1048576).toFixed(0);
      toast.success(`Yuklab olinmoqda: ${data.filename} (${mb} MB)`);
      window.location.href = `${API}${data.url}`;   // browser downloads the .apk natively
    } catch (e) { if (e.message !== 'unauthorized') toast.error('Xato: ' + (e.message || '')); }
    finally { setApkBusy(false); }
  };

  const onEnablePush = async () => {
    if (!pushSupported()) { toast.error('Brauzer push‘ni qo‘llamaydi. iPhone’da avval “Bosh ekranga qo‘shing”.'); return; }
    const res = await enablePush(authFetch, API);
    setPushState(pushPermissionState());
    if (res.ok) { toast.success('Push yoqildi'); sendTestPush(authFetch, API); }
    else if (res.reason === 'denied') toast.error('Ruxsat berilmadi');
    else toast.error('Push yoqilmadi: ' + res.reason);
  };

  const startVoice = () => {
    const Rec = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Rec) { toast.error('Ovozli kiritish qo‘llab-quvvatlanmaydi'); return; }
    try {
      const rec = new Rec();
      rec.lang = 'uz-UZ'; rec.interimResults = false; rec.maxAlternatives = 1;
      rec.onstart = () => setListening(true);
      rec.onend = () => setListening(false);
      rec.onerror = () => setListening(false);
      rec.onresult = (e) => {
        const t = e.results[0][0].transcript;
        setNewTask(prev => (prev ? prev + ' ' : '') + t);
      };
      rec.start();
    } catch { setListening(false); }
  };

  // ---- emulator ----
  const EMU_W = 720, EMU_H = 1280; // fallback device size; emuTouchScale reads the real frame size live
  const fetchEmuList = useCallback(async (autostream = false) => {
    try {
      const r = await authFetch(`${API}/emulator/list`);
      if (!r.ok) return;
      const d = await r.json();
      setEmuMaxSlots(d.maxSlots || 3);
      const list = d.emulators || [];
      setEmuList(list);
      setEmuStatuses(prev => { const n = { ...prev }; list.forEach(e => { if (n[e.slot] === undefined) n[e.slot] = e.status; }); return n; });
      if (autostream) list.forEach(e => { if (e.status === 'running') { socketRef.current?.emit('emulator:start_stream', { slot: e.slot }); setEmuStreaming(s => ({ ...s, [e.slot]: true })); } });
    } catch (e) {}
  }, [authFetch]);
  const emuStartStream = (slot) => { socketRef.current?.emit('emulator:start_stream', { slot }); setEmuStreaming(s => ({ ...s, [slot]: true })); };
  const emuStopStream = (slot) => { socketRef.current?.emit('emulator:stop_stream', { slot }); setEmuStreaming(s => ({ ...s, [slot]: false })); setEmuFrames(f => { const n = { ...f }; delete n[slot]; return n; }); };
  const emuStart = async (slot) => {
    setEmuBusy(b => ({ ...b, [slot]: true })); setEmuStatuses(s => ({ ...s, [slot]: 'starting' }));
    try { await authFetch(`${API}/emulator/start`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ slot }) }); emuStartStream(slot); setTimeout(() => fetchEmuList(), 1500); }
    catch (e) { toast.error('Xato'); setEmuBusy(b => ({ ...b, [slot]: false })); }
  };
  const emuStop = async (slot) => {
    setEmuBusy(b => ({ ...b, [slot]: true }));
    try { await authFetch(`${API}/emulator/stop`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ slot }) }); emuStopStream(slot); setEmuStatuses(s => ({ ...s, [slot]: 'stopped' })); }
    catch (e) { toast.error('Xato'); } finally { setEmuBusy(b => ({ ...b, [slot]: false })); }
  };
  const emuReconnect = async (slot) => {
    setEmuBusy(b => ({ ...b, [slot]: true })); setEmuStatuses(s => ({ ...s, [slot]: 'reconnecting' }));
    try { await authFetch(`${API}/emulator/reconnect`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ slot }) }); }
    catch (e) { toast.error('Xato'); setEmuBusy(b => ({ ...b, [slot]: false })); }
  };
  const addEmulator = () => {
    const used = new Set((emuList || []).map(e => e.slot)); let next = -1;
    for (let s = 0; s < emuMaxSlots; s++) { if (!used.has(s)) { next = s; break; } }
    if (next < 0) { toast(`Maksimal ${emuMaxSlots} ta emulator`); return; }
    setEmuList(prev => [...prev, { slot: next, status: 'starting' }]); emuStart(next);
  };
  const openFull = (slot) => { if (!emuStreamingRef.current[slot]) emuStartStream(slot); setEmuFull(slot); };
  const emuKey = (slot, keycode) => socketRef.current?.emit('emulator:keyevent', { slot, keycode });
  const emuAppDrawer = (slot) => socketRef.current?.emit('emulator:app_drawer', { slot });
  // Scale to the ACTUAL device pixels using the live frame's own size (Redroid is 720×1280,
  // not the hardcoded 1080×1920 — that mismatch made taps land ~1.5× off).
  const emuTouchScale = (el, cx, cy) => {
    const m = el.querySelector('video, img');
    const r = (m || el).getBoundingClientRect();
    const dw = (m && (m.videoWidth || m.naturalWidth)) || EMU_W;
    const dh = (m && (m.videoHeight || m.naturalHeight)) || EMU_H;
    return { x: (cx - r.left) * (dw / (r.width || 1)), y: (cy - r.top) * (dh / (r.height || 1)) };
  };
  const EMU_LONGPRESS_MS = 480;
  const emuTouchStart = (slot, e) => {
    const t = e.touches[0]; if (!t) return;
    const el = e.currentTarget;
    const lp = setTimeout(() => {
      const d = emuDragRef.current[slot]; if (!d || d.moved || d.fired) return;
      d.fired = 'long';
      const to = emuTouchScale(el, t.clientX, t.clientY);
      socketRef.current?.emit('emulator:longpress', { slot, x: to.x, y: to.y });
    }, EMU_LONGPRESS_MS);
    emuDragRef.current[slot] = { x: t.clientX, y: t.clientY, time: Date.now(), moved: false, fired: false, lp };
  };
  const emuTouchMove = (slot, e) => {
    const d = emuDragRef.current[slot]; if (!d || d.fired) return; const t = e.touches[0]; if (!t) return;
    if (Math.abs(t.clientX - d.x) > 12 || Math.abs(t.clientY - d.y) > 12) d.moved = true;
  };
  const emuTouchEnd = (slot, e) => {
    const d = emuDragRef.current[slot]; if (!d) return; clearTimeout(d.lp);
    emuDragRef.current[slot] = null;
    if (d.fired) return; // long-press already sent on the timer
    const t = e.changedTouches[0]; if (!t) return;
    const el = e.currentTarget; const dx = t.clientX - d.x, dy = t.clientY - d.y;
    // duration sent raw; the backend clamps it to ≤400ms so swipes feel snappy.
    if (Math.sqrt(dx * dx + dy * dy) >= 14) {
      const from = emuTouchScale(el, d.x, d.y), to = emuTouchScale(el, t.clientX, t.clientY);
      socketRef.current?.emit('emulator:swipe', { slot, x1: from.x, y1: from.y, x2: to.x, y2: to.y, duration: Date.now() - d.time });
    } else {
      const to = emuTouchScale(el, t.clientX, t.clientY);
      socketRef.current?.emit('emulator:tap', { slot, x: to.x, y: to.y });
    }
  };

  const visibleTasks = useMemo(() => {
    if (projectId && projectId !== 'all') return tasks.filter(x => String(x.project_id) === String(projectId));
    return tasks; // 'all' / unset → every project's tasks (cards show the project name)
  }, [tasks, projectId]);
  const allMode = projectId === 'all';

  const canApprove = (t) => !t.approved_at && Array.isArray(t.sub_tasks) && t.sub_tasks.length > 0
    && t.sub_tasks.every(st => ['completed', 'failed', 'cancelled'].includes(st.status))
    && t.sub_tasks.some(st => st.status === 'completed');

  // ---- render ----
  return (
    <div className="min-h-screen bg-gray-900 text-gray-100" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <Toaster position="top-center" toastOptions={{ style: { background: '#1e293b', color: '#f1f5f9', border: '1px solid #334155' } }} />

      {/* Header */}
      <header className="sticky top-0 z-20 bg-gray-900/95 backdrop-blur border-b border-gray-800 px-4 py-3 flex items-center justify-between"
              style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.5rem)' }}>
        <div className="flex items-center gap-2 font-bold text-lg">
          <img src="/logo192.png" alt="" className="kh-brand-logo w-7 h-7 rounded-lg object-cover shrink-0" />
          Khorezmai
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onEnablePush}
                  className={`text-sm px-3 py-1.5 rounded-full border ${pushState === 'granted' ? 'bg-green-500/15 text-green-300 border-green-500/30' : 'bg-gray-800 border-gray-700 text-gray-200'}`}>
            {pushState === 'granted' ? '🔔 Push yoniq' : '🔔 Push'}
          </button>
          <button onClick={() => { localStorage.setItem('kh_force_desktop', '1'); window.location.assign('/'); }}
                  className="text-xs px-2 py-1.5 rounded-full border border-gray-700 text-gray-400">🖥</button>
        </div>
      </header>

      {!openTask && (
        <main className="px-4 py-4 space-y-4 max-w-xl mx-auto">
          {/* Project picker */}
          <select value={projectId} onChange={e => setProjectId(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-3 text-base">
            {projects.length === 0 && <option value="">Loyiha yo‘q</option>}
            {projects.length > 0 && <option value="all">🗂 Barchasi loyihalar</option>}
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>

          {/* 🧠 Loyiha agenti — faqat aniq loyiha tanlanganda */}
          {projectId && projectId !== 'all' && !isClient && (
            <button onClick={openAgent}
                    className="w-full px-4 py-3 rounded-lg bg-primary-600 hover:bg-primary-700 font-semibold text-base flex items-center justify-center gap-2">
              🧠 Agent bilan suhbat
            </button>
          )}

          {/* Download the built APK to this phone */}
          {projectId && projectId !== 'all' && !isClient && (
            <button onClick={downloadApk} disabled={apkBusy}
                    className="w-full px-4 py-3 rounded-lg bg-emerald-600 disabled:opacity-40 font-semibold text-base flex items-center justify-center gap-2">
              {apkBusy ? '⏳ Havola tayyorlanmoqda…' : '📥 APK (.apk) yuklab olish'}
            </button>
          )}

          {/* 🧑‍💼 Nazoratchi — faqat aniq loyiha tanlanganda */}
          {sv && !allMode && !isClient && (() => {
            const items = sv.items || [];
            const S_ = {
              proposed: { l: '📝 Taklif', c: 'bg-amber-500/20 text-amber-300' },
              approved: { l: '⏳ Navbatda', c: 'bg-sky-500/20 text-sky-300' },
              running:  { l: '🔄 Sinalmoqda', c: 'bg-primary-600/20 text-primary-300' },
              passed:   { l: '✅ O‘tdi', c: 'bg-emerald-500/20 text-emerald-300' },
              verified: { l: '✅ Tuzatildi', c: 'bg-emerald-500/20 text-emerald-300' },
              fixing:   { l: '🛠 Tuzatilmoqda', c: 'bg-primary-600/20 text-primary-300' },
              waiting:  { l: '🔁 Vazifa/deploy kutilmoqda', c: 'bg-sky-500/20 text-sky-300' },
              question: { l: '❓ Savol', c: 'bg-red-500/20 text-red-300' },
              skipped:  { l: '⏭ O‘tkazildi', c: 'bg-gray-700 text-gray-400' },
              error:    { l: '⚠️ Xato', c: 'bg-red-500/20 text-red-300' },
            };
            const active = items.filter(i => ['question', 'proposed', 'running', 'fixing', 'approved', 'waiting'].includes(i.status));
            const done = items.filter(i => !['question', 'proposed', 'running', 'fixing', 'approved', 'waiting'].includes(i.status));
            const nProp = items.filter(i => i.status === 'proposed').length;
            const nQ = items.filter(i => i.status === 'question').length;
            const en = sv.state && sv.state.enabled;
            const head = !en ? 'o‘chiq' : nQ ? `❓ ${nQ} ta savol` : nProp ? `📝 ${nProp} ta taklif` : sv.running ? '🔄 sinalmoqda' : active.length ? `${active.length} ta ish` : 'kuzatmoqda';
            return (
              <div className={`bg-gray-800/60 border rounded-lg overflow-hidden ${nQ ? 'border-red-500/50' : nProp ? 'border-amber-500/50' : 'border-gray-700'}`}>
                <button onClick={() => setSvOpen(o => !o)} className="w-full px-3 py-3 flex items-center justify-between text-base font-semibold">
                  <span>🧑‍💼 Nazoratchi — <span className={nQ ? 'text-red-300' : nProp ? 'text-amber-300' : 'text-gray-300'}>{head}</span></span>
                  <span className="text-gray-400">{svOpen ? '▾' : '▸'}</span>
                </button>
                {svOpen && (
                  <div className="px-3 pb-3 space-y-2">
                    {/* Boshqaruv */}
                    <div className="flex items-center gap-2 flex-wrap text-xs">
                      <button disabled={svBusy === 'state'} onClick={() => svSetState({ enabled: !en })}
                              className={`px-2.5 py-1.5 rounded-lg font-semibold ${en ? 'bg-emerald-600' : 'bg-gray-700'}`}>
                        {en ? '● Yoqilgan' : '○ O‘chiq'}
                      </button>
                      <button disabled={svBusy === 'state' || !en} onClick={() => svSetState({ auto_approve: !(sv.state && sv.state.auto_approve) })}
                              title="Avto rejim: takliflarni so'ramasdan o'zi sinaydi"
                              className={`px-2.5 py-1.5 rounded-lg ${sv.state && sv.state.auto_approve ? 'bg-primary-700' : 'bg-gray-700 text-gray-300'} disabled:opacity-40`}>
                        🤖 Avto {sv.state && sv.state.auto_approve ? 'yoniq' : 'o‘chiq'}
                      </button>
                      <button disabled={svBusy === 'scan' || !en} onClick={svScan} className="px-2.5 py-1.5 rounded-lg bg-gray-700 disabled:opacity-40">
                        {svBusy === 'scan' ? '⏳ Qidirilmoqda…' : '🔍 Hozir tekshir'}
                      </button>
                      {nProp > 1 && <button disabled={svBusy === 'all'} onClick={svApproveAll} className="px-2.5 py-1.5 rounded-lg bg-emerald-600 font-semibold">✅ Hammasini bajar ({nProp})</button>}
                    </div>
                    {!en && <div className="text-[11px] text-gray-400">Yoqilsa: tugagan ishlarni o‘zi topadi, sinov taklif qiladi, ✅ desangiz sinaydi, topilmani vazifa qilib tuzatadi va qayta sinaydi.</div>}

                    {/* O'z stsenariyim */}
                    <div className="flex gap-2">
                      <input value={svWish} onChange={e => setSvWish(e.target.value)} placeholder="Shuni sina: masalan «pochta olishni sina»"
                             onKeyDown={e => { if (e.key === 'Enter') svPropose(); }}
                             className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm" />
                      <button disabled={svBusy === 'propose' || svWish.trim().length < 5} onClick={svPropose}
                              className="px-3 py-2 rounded-lg bg-primary-700 disabled:opacity-40 text-sm font-semibold">▶</button>
                    </div>
                    {renderTargetChips(svPick, setSvPick)}
                    <div className="flex gap-1.5 text-[11px]">
                      <button onClick={() => setSvVoice(v => !v)} className={`px-2 py-1 rounded-full border ${svVoice ? 'bg-primary-700 border-primary-600 text-white' : 'border-gray-700 text-gray-400'}`}>🔊 Ovozli</button>
                      <button onClick={() => setSvRecord(v => !v)} title="Sinov videoga yoziladi — o'tsa Telegram'ga ham keladi (mijozga yuborish uchun)"
                              className={`px-2 py-1 rounded-full border ${svRecord ? 'bg-rose-700 border-rose-600 text-white' : 'border-gray-700 text-gray-400'}`}>🎥 Video</button>
                    </div>

                    {/* Ketayotgan sinov */}
                    {sv.running && (
                      <div className="bg-primary-900/40 border border-primary-600/30 rounded-lg p-2.5">
                        <div className="flex items-center justify-between">
                          <div className="text-xs font-semibold text-primary-200 animate-pulse">🔄 Sinalmoqda · {Math.round((Date.now() - sv.running.startedAt) / 60000)} daq</div>
                          <button onClick={svStop} className="text-[11px] px-2 py-1 rounded-lg bg-red-600/80">⏹</button>
                        </div>
                        <pre className="text-[10px] text-gray-300 whitespace-pre-wrap break-words max-h-32 overflow-auto mt-1.5 bg-gray-900/60 rounded p-1.5">
                          {(sv.running.log || []).slice(-8).join('\n')}
                        </pre>
                      </div>
                    )}

                    {/* Faol elementlar */}
                    {active.length === 0 && en && !sv.running && <div className="text-[11px] text-gray-500">Hozir taklif yo‘q — tugagan ishlar sinalgan. Yangi vazifa tugasa o‘zi taklif qiladi.</div>}
                    {active.map(it => {
                      const st = S_[it.status] || { l: it.status, c: 'bg-gray-700' };
                      const exp = !!svExpanded[it.id];
                      const b = (a) => svBusy === `${it.id}:${a}`;
                      return (
                        <div key={it.id} className={`bg-gray-900/60 border rounded-lg p-2.5 ${it.status === 'question' ? 'border-red-500/40' : it.status === 'proposed' ? 'border-amber-500/40' : 'border-gray-700'}`}>
                          <div className="flex items-center gap-1.5 flex-wrap mb-1">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${st.c}`}>{st.l}</span>
                            {it.attempt > 1 && <span className="text-[10px] text-gray-400">qayta sinov {it.attempt - 1}</span>}
                            {it.source_task_id && <button onClick={() => setOpenId(it.source_task_id)} className="text-[10px] text-gray-400 underline">#{it.source_task_id}</button>}
                          </div>
                          <div className="text-xs font-semibold text-gray-100 break-words">{it.title}</div>
                          {it.scenario && (
                            <button onClick={() => setSvExpanded(x => ({ ...x, [it.id]: !exp }))} className="text-left w-full">
                              <div className={`text-[11px] text-gray-400 mt-1 whitespace-pre-wrap break-words ${exp ? '' : 'line-clamp-3'}`}>{it.scenario}</div>
                              <div className="text-[10px] text-primary-300 mt-0.5">{exp ? '▴ yig‘ish' : '▾ to‘liq stsenariy'}</div>
                            </button>
                          )}
                          {it.note && it.status !== 'proposed' && <div className="text-[10px] text-gray-500 mt-1 whitespace-pre-wrap break-words">{String(it.note).slice(0, 400)}</div>}
                          {it.video && <RunVideo video={it.video} apiUrl={API} dark />}
                          {it.status === 'fixing' && Array.isArray(it.fix_tasks) && (
                            <div className="flex gap-1.5 flex-wrap mt-1">
                              {it.fix_tasks.map(t => (
                                <button key={t.id} onClick={() => setOpenId(t.id)} className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary-600/20 text-primary-300">
                                  🛠 #{t.id} · {t.approved_at ? (t.deploy_stage || 'deploy') : (STATUS[t.status]?.label || t.status)}
                                </button>
                              ))}
                              <span className="text-[10px] text-gray-500 self-center">deploy tugagach qayta sinayman</span>
                            </div>
                          )}
                          {it.status === 'question' && (
                            <div className="mt-2 space-y-1.5">
                              <div className="text-[11px] text-red-200 whitespace-pre-wrap break-words">❓ {it.question}</div>
                              {Array.isArray(it.options) ? (
                                <div className="flex flex-wrap gap-2">
                                  {it.options.filter(o => o !== 'skip').map(o => (
                                    <button key={o} disabled={b('answer')} onClick={() => svAct(it.id, 'answer', { answer: o })}
                                            className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold disabled:opacity-50 ${o === 'accept' ? 'bg-emerald-700' : 'bg-primary-700'}`}>
                                      {({ 'retry-fix': '🔁 Tuzatishni qayta yurit', accept: '✅ O‘tdi deb qabul', fix: '🛠 Tuzatish och', retry: '🔁 Qayta sina' })[o] || '🔁 Qayta sina'}
                                    </button>
                                  ))}
                                  <button disabled={b('skip')} onClick={() => svAct(it.id, 'skip')} className="px-3 py-2 rounded-lg bg-gray-700 text-xs disabled:opacity-50">⏭ O‘tkazib yubor</button>
                                </div>
                              ) : (
                                <div className="flex gap-2">
                                  <input value={svAnswer[it.id] || ''} onChange={e => setSvAnswer(a => ({ ...a, [it.id]: e.target.value }))}
                                         placeholder="Javobingiz…" className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm" />
                                  <button disabled={b('answer') || !(svAnswer[it.id] || '').trim()} onClick={() => svAct(it.id, 'answer', { answer: svAnswer[it.id] })}
                                          className="px-3 py-2 rounded-lg bg-primary-700 text-xs font-semibold disabled:opacity-40">📨</button>
                                  <button disabled={b('skip')} onClick={() => svAct(it.id, 'skip')} className="px-2 py-2 rounded-lg bg-gray-700 text-xs">⏭</button>
                                </div>
                              )}
                            </div>
                          )}
                          {it.status === 'proposed' && (() => {
                            const pk = pickFor(it);
                            const n = (pk.slots || []).length + (pk.web ? 1 : 0);
                            return (
                            <div className="mt-2 space-y-1.5">
                              {renderTargetChips(pk, (p) => setSvItemPick(m => ({ ...m, [it.id]: p })), true)}
                              <div className="flex gap-2">
                              <button disabled={b('approve') || !n} onClick={() => svAct(it.id, 'approve', { slots: pk.slots || [], webModuleId: pk.web || null })}
                                      className="flex-1 px-3 py-2.5 rounded-lg bg-emerald-600 text-xs font-semibold disabled:opacity-50">
                                {b('approve') ? '⏳…' : '✅ Bajar'}
                              </button>
                              <button disabled={b('skip')} onClick={() => svAct(it.id, 'skip')} className="px-3 py-2.5 rounded-lg bg-gray-700 text-xs">⏭</button>
                              </div>
                            </div>
                            );
                          })()}
                          {it.status !== 'proposed' && (it.targets?.mobileModuleId || it.targets?.webModuleId) && (
                            <div className="text-[10px] text-gray-500 mt-1">
                              🎯 {[...((it.targets.slots || []).map(sl => `📱 E${sl + 1}`)), it.targets.webModuleId ? '🌐 sayt' : null].filter(Boolean).join(' + ')
                                  || (it.targets.devices >= 2 ? '📱 2 ta emulator' : '📱 1 ta emulator')}
                              {it.targets.autoDevices ? ' · matndan 2 rol aniqlandi' : ''}
                            </div>
                          )}
                          {it.status === 'approved' && (
                            <div className="flex gap-2 mt-2">
                              <span className="flex-1 text-[11px] text-sky-300 self-center">navbatda — emulator bo‘shashi bilan boshlanadi</span>
                              <button disabled={b('skip')} onClick={() => svAct(it.id, 'skip')} className="px-3 py-1.5 rounded-lg bg-gray-700 text-xs">⏭</button>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* 🎥 Oxirgi etap videolari — tarix yopiq bo'lsa ham ko'rinsin (mijozga yuborish uchun) */}
                    {!svShowDone && (() => {
                      const vids = done.filter(it => it.video && it.video.status === 'ready' && it.video.clientReady).slice(0, 3);
                      return vids.length > 0 ? (
                        <div className="space-y-1">
                          <div className="text-[11px] text-gray-400">🎥 Oxirgi etap videolari</div>
                          {vids.map(it => <RunVideo key={it.id} video={it.video} apiUrl={API} dark />)}
                        </div>
                      ) : null;
                    })()}
                    {/* Tarix */}
                    {done.length > 0 && (
                      <button onClick={() => setSvShowDone(v => !v)} className="text-[11px] text-gray-400">{svShowDone ? '▴ tarixni yashirish' : `▾ oxirgi natijalar (${done.length})`}</button>
                    )}
                    {svShowDone && done.slice(0, 12).map(it => {
                      const st = S_[it.status] || { l: it.status, c: 'bg-gray-700' };
                      const v = it.verdict;
                      return (
                        <div key={it.id} className="bg-gray-900/40 border border-gray-800 rounded-lg p-2">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${st.c}`}>{st.l}</span>
                            <span className="text-[11px] text-gray-200 break-words">{it.title}</span>
                          </div>
                          {(v && v.summary) ? <div className="text-[10px] text-gray-400 mt-0.5 break-words">{String(v.summary).slice(0, 220)}</div>
                            : it.note ? <div className="text-[10px] text-gray-500 mt-0.5 break-words">{String(it.note).slice(0, 160)}</div> : null}
                          {Array.isArray(it.shots) && it.shots.length > 0 && (
                            <div className="flex gap-1 mt-1 overflow-x-auto">
                              {it.shots.slice(0, 6).map((u, i) => <img key={i} src={`${API}${u}`} alt="" className="h-14 rounded border border-gray-700" />)}
                            </div>
                          )}
                          {it.video && <RunVideo video={it.video} apiUrl={API} dark />}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}

          {/* ⏳ Fon jarayonlari — logo/TZ/kalit tuzatish. Ish stolida bor edi,
              telefonda YO'Q edi: ya'ni "Khorezmai o'zi to'g'irlasin" hisobotini
              telefondan ko'rib bo'lmasdi. */}
          {jobs.length > 0 && (
            <div className="bg-gray-800/60 border border-gray-700 rounded-lg overflow-hidden">
              <button onClick={() => setPanel(panel === 'jobs' ? '' : 'jobs')}
                      className="w-full px-3 py-3 flex items-center justify-between text-base font-semibold">
                <span>⏳ Fon jarayonlari ({jobs.length}){jobs.some(j => j.status === 'running') ? ' 🔄' : ''}</span>
                <span className="text-gray-400">{panel === 'jobs' ? '▾' : '▸'}</span>
              </button>
              {panel === 'jobs' && (
                <div className="px-3 pb-3 space-y-2">
                  {jobs.map(j => {
                    const st = JOB_ST[j.status] || ['', 'bg-gray-700 text-gray-300'];
                    return (
                      <div key={j.id} className="bg-gray-900/60 border border-gray-700 rounded-lg p-2.5">
                        <div className="flex items-start justify-between gap-2">
                          <div className="text-sm font-semibold flex-1">{j.title || 'Jarayon #' + j.id}</div>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${st[1]}`}>{st[0]}</span>
                        </div>
                        {!!j.total && (
                          <div className="h-1 bg-gray-700 rounded mt-1.5 overflow-hidden">
                            <div className="h-full bg-primary-600" style={{ width: `${Math.min(100, Math.round((j.done / j.total) * 100))}%` }} />
                          </div>
                        )}
                        {j.detail && (
                          <div className="text-[11px] text-gray-300 mt-1.5 whitespace-pre-wrap leading-relaxed max-h-48 overflow-auto bg-gray-800/70 rounded px-2 py-1.5">
                            {j.detail}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* 💥 Crashlar */}
          {visibleCrashes.length > 0 && (
            <div className="bg-gray-800/60 border border-gray-700 rounded-lg overflow-hidden">
              <button onClick={() => setPanel(panel === 'crashes' ? '' : 'crashes')}
                      className="w-full px-3 py-3 flex items-center justify-between text-base font-semibold">
                <span>💥 Crashlar ({visibleCrashes.length})</span>
                <span className="text-gray-400">{panel === 'crashes' ? '▾' : '▸'}</span>
              </button>
              {panel === 'crashes' && (
                <div className="px-3 pb-3 space-y-2">
                  {visibleCrashes.slice(0, 20).map(c => {
                    const info = crashInfo[c.id];
                    const tr = crashTrace[c.id];
                    const open = crashOpen === c.id;
                    return (
                    <div key={c.id} className="bg-gray-900/60 border border-gray-700 rounded-lg p-2.5">
                      <button onClick={() => openCrash(c)} className="w-full text-left">
                        <div className="flex items-center gap-1.5 flex-wrap mb-1">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${c.fatal ? 'bg-red-500/20 text-red-300' : 'bg-amber-500/20 text-amber-300'}`}>
                            {c.fatal ? 'fatal' : 'non-fatal'}
                          </span>
                          {c.occurrences > 1 && <span className="text-[10px] text-gray-400">×{c.occurrences}</span>}
                          {c.fixed_version && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">✅ {c.fixed_version} da tuzatilgan</span>}
                          {c.regressed_at && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/30 text-red-200">🔴 QAYTDI</span>}
                          {c.task_id && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary-600/20 text-primary-300">🛠 #{c.task_id}</span>}
                          <span className="text-gray-500 text-[10px] ml-auto">{open ? '▾' : '▸'}</span>
                        </div>
                        <div className="text-xs text-gray-200 break-words">{String(c.message || '').slice(0, 200)}</div>
                        <div className="text-[10px] text-gray-500 mt-1">
                          {c.project_name || ''} {c.app_version ? '· v' + c.app_version : ''} {c.platform ? '· ' + c.platform : ''}
                          {c.screen ? ' · ' + c.screen : ''}
                        </div>
                      </button>

                      {open && (
                        <div className="mt-2 pt-2 border-t border-gray-700 space-y-2">
                          {/* Sabab (stack) — ro'yxatda kelmaydi, ochilganda so'raladi */}
                          <div>
                            <div className="text-[10px] text-gray-400 mb-1">Sabab (stack)</div>
                            <pre className="text-[10px] text-gray-300 whitespace-pre-wrap break-words max-h-64 overflow-auto bg-gray-800/70 rounded p-2">
                              {info ? (info.stack || '(stack yo‘q)') : 'Yuklanmoqda…'}
                            </pre>
                          </div>

                          {/* Tuzatish izi — nima qilindi, qayerda bor */}
                          {c.task_id && (
                            <div className="bg-gray-800/60 rounded p-2">
                              <div className="text-[10px] text-gray-400 mb-1">🔎 Tuzatish izi</div>
                              {!tr ? <div className="text-[11px] text-gray-500">Yuklanmoqda…</div> : (
                                <div className="space-y-1">
                                  {(tr.modules || []).map((m, i) => (
                                    <div key={i} className="text-[11px]">
                                      <div className="text-gray-300">📦 {m.name}{(m.commits || []).length ? ' · ' + m.commits[0].sha : ''}</div>
                                      <div className="flex gap-2 mt-0.5">
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${m.test && m.test.found ? 'bg-emerald-500/20 text-emerald-300' : 'bg-gray-700 text-gray-400'}`}>
                                          {m.test && m.test.found ? '✅' : '❌'} 🧪 test
                                        </span>
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${m.production && m.production.found ? 'bg-emerald-500/20 text-emerald-300' : 'bg-gray-700 text-gray-400'}`}>
                                          {m.production && m.production.found ? '✅' : '❌'} 🚀 production
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                  {!(tr.modules || []).length && <div className="text-[11px] text-gray-500">Hali commit topilmadi.</div>}
                                </div>
                              )}
                            </div>
                          )}

                          {!isClient && <div className="grid grid-cols-2 gap-2">
                            {!c.task_id ? (
                              <button onClick={() => crashFixTask(c)} disabled={crashBusy === c.id}
                                      className="px-3 py-2.5 rounded-lg bg-primary-700 disabled:opacity-50 text-xs font-semibold">
                                {crashBusy === c.id ? '⏳…' : '🛠 Tuzatish vazifasi'}
                              </button>
                            ) : (
                              <button onClick={() => { setPanel(''); setOpenId(c.task_id); }}
                                      className="px-3 py-2.5 rounded-lg bg-primary-700 text-xs font-semibold">
                                🛠 Vazifani ochish #{c.task_id}
                              </button>
                            )}
                            <button onClick={() => crashDelete(c)} disabled={crashBusy === c.id}
                                    className="px-3 py-2.5 rounded-lg bg-gray-700 disabled:opacity-50 text-xs font-semibold">🗑 O‘chirish</button>
                          </div>}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* 🐞 Topilmalar */}
          {visibleFindings.length > 0 && (
            <div className="bg-gray-800/60 border border-gray-700 rounded-lg overflow-hidden">
              <button onClick={() => setPanel(panel === 'findings' ? '' : 'findings')}
                      className="w-full px-3 py-3 flex items-center justify-between text-base font-semibold">
                <span>🐞 Topilmalar ({visibleFindings.filter(f => f.status === 'new').length}/{visibleFindings.length})</span>
                <span className="text-gray-400">{panel === 'findings' ? '▾' : '▸'}</span>
              </button>
              {panel === 'findings' && (
                <div className="px-3 pb-3 space-y-2">
                  {visibleFindings.slice(0, 15).map(f => (
                    <div key={f.id} className="bg-gray-900/60 border border-gray-700 rounded-lg p-2.5">
                      <div className="text-xs font-semibold text-gray-100 break-words">{f.title}</div>
                      {f.detail && <div className="text-[11px] text-gray-400 mt-0.5 break-words">{String(f.detail).slice(0, 200)}</div>}
                      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                        {f.occurrences > 1 && <span className="text-[10px] text-gray-400">×{f.occurrences}</span>}
                        {f.fix_task_id
                          ? <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary-600/20 text-primary-300">🛠 #{f.fix_task_id}</span>
                          : f.status === 'new' && !isClient && (
                            <>
                              <button onClick={async () => {
                                        const r = await authFetch(`${API}/agent-findings/${f.id}/task`, { method: 'POST' });
                                        if (r.ok) { toast.success('Vazifa yaratildi'); fetchExtras(); fetchTasks(); } else toast.error('Yaratilmadi');
                                      }}
                                      className="px-2.5 py-1 rounded-lg bg-primary-700 text-[11px] font-semibold">✅ Vazifa</button>
                              <button onClick={async () => {
                                        const r = await authFetch(`${API}/agent-findings/${f.id}/dismiss`, { method: 'POST' });
                                        if (r.ok) { toast.success('Rad etildi'); fetchExtras(); }
                                      }}
                                      className="px-2.5 py-1 rounded-lg bg-gray-700 text-[11px]">⏭ Rad etish</button>
                            </>
                          )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 🩺 Salomatlik — faqat ruxsat bo'lsa (admin) yuklanadi */}
          {Array.isArray(health) && health.length > 0 && (() => {
            const bad = health.filter(h => h.status === 'fail');
            const warn = health.filter(h => h.status === 'warn');
            return (
              <div className="bg-gray-800/60 border border-gray-700 rounded-lg overflow-hidden">
                <button onClick={() => setPanel(panel === 'health' ? '' : 'health')}
                        className="w-full px-3 py-3 flex items-center justify-between text-base font-semibold">
                  <span>🩺 Salomatlik — {bad.length ? `❌ ${bad.length} ta muammo` : warn.length ? `⚠️ ${warn.length} ta ogohlantirish` : '✅ hammasi joyida'}</span>
                  <span className="text-gray-400">{panel === 'health' ? '▾' : '▸'}</span>
                </button>
                {panel === 'health' && (
                  <div className="px-3 pb-3 space-y-1">
                    {[...bad, ...warn, ...health.filter(h => h.status === 'ok')].slice(0, 40).map(h => (
                      <div key={h.id} className="flex items-center gap-2 text-xs bg-gray-900/50 rounded-lg px-2 py-1.5">
                        <span>{h.status === 'ok' ? '✅' : h.status === 'warn' ? '⚠️' : '❌'}</span>
                        <span className="flex-1 truncate">{h.name}</span>
                        {h.last_detail && <span className="text-[10px] text-gray-500 truncate max-w-[40%]">{String(h.last_detail).slice(0, 40)}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {!isClient && (<>
          {/* Emulator (collapsible) */}
          <div className="bg-gray-800/60 border border-gray-700 rounded-lg overflow-hidden">
            <button
              onClick={() => { const n = !emuOpen; setEmuOpen(n); if (n) fetchEmuList(true); else { socketRef.current?.emit('emulator:stop_stream'); setEmuStreaming({}); } }}
              className="w-full px-3 py-3 flex items-center justify-between text-base font-semibold">
              <span>📱 Emulator{emuList.length ? ` (${emuList.length})` : ''}</span>
              <span className="text-gray-400">{emuOpen ? '▾' : '▸'}</span>
            </button>
            {emuOpen && (
              <div className="px-3 pb-3 space-y-2">
                {emuList.length === 0 && (
                  <button onClick={addEmulator} className="w-full py-2.5 rounded-lg bg-emerald-600 text-sm font-semibold">▶ Emulatorni ishga tushirish</button>
                )}
                {[...emuList].sort((a, b) => a.slot - b.slot).map(emu => {
                  const slot = emu.slot;
                  const st = emuStatuses[slot] || emu.status || 'not_created';
                  const running = st === 'running';
                  const busy = !!emuBusy[slot];
                  const frame = emuFrames[slot];
                  return (
                    <div key={slot} className="bg-gray-900 border border-gray-700 rounded-lg p-2 flex items-center gap-3">
                      <div onClick={() => running && openFull(slot)} className="w-14 shrink-0 bg-black rounded-lg overflow-hidden border border-gray-700" style={{ aspectRatio: '9 / 16' }}>
                        {frame
                          ? <img src={frame} className="w-full h-full object-contain" alt="" />
                          : <div className="w-full h-full flex items-center justify-center text-[9px] text-gray-600">{running ? '…' : '○'}</div>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold">Emulator {slot + 1}</div>
                        <div className={`text-xs ${running ? 'text-green-400' : st === 'error' ? 'text-red-400' : 'text-gray-400'}`}>{EMU_ST[st] || st}</div>
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        {running ? (
                          <>
                            <button onClick={() => openFull(slot)} className="px-2.5 py-1.5 text-xs bg-primary-700 rounded-lg font-semibold">⛶ Ochish</button>
                            <button onClick={() => emuStop(slot)} disabled={busy} className="px-2.5 py-1.5 text-xs bg-red-600 rounded-lg disabled:opacity-50">⏹ To‘xtatish</button>
                          </>
                        ) : (
                          <button onClick={() => emuStart(slot)} disabled={busy} className="px-2.5 py-1.5 text-xs bg-green-600 rounded-lg disabled:opacity-50 font-semibold">{busy ? '⏳…' : '▶ Boshlash'}</button>
                        )}
                      </div>
                    </div>
                  );
                })}
                {emuList.length > 0 && emuList.length < emuMaxSlots && (
                  <button onClick={addEmulator} className="w-full py-2 rounded-lg bg-emerald-600/80 text-xs font-semibold">➕ Yangi emulator</button>
                )}
              </div>
            )}
          </div>
          </>)}

          {/* Add task */}
          <div className="bg-gray-800/60 border border-gray-700 rounded-lg p-3 space-y-2">
            <textarea value={newTask} onChange={e => setNewTask(e.target.value)} rows={3}
                      placeholder="Vazifa yozing… (masalan: login sahifasiga 'parolni unutdingizmi' qo‘shing)"
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-3 text-base resize-none" />
            <div className="flex gap-2">
              <button onClick={startVoice}
                      className={`px-3 py-3 rounded-lg border text-base ${listening ? 'bg-red-500/20 border-red-500/40 text-red-300 animate-pulse' : 'bg-gray-900 border-gray-700'}`}>🎤</button>
              <button onClick={() => setPlanReview(!planReview)}
                      title="Reja tasdig'i: kod yozishdan oldin nima o'zgarishini ko'rsatib, tasdiqingizni kutadi"
                      className={`px-3 py-3 rounded-lg border text-sm ${planReview ? 'bg-indigo-500/20 border-indigo-500/50 text-indigo-200' : 'bg-gray-900 border-gray-700 text-gray-400'}`}>📝{planReview ? '✓' : ''}</button>
              <button onClick={addTask} disabled={adding || !newTask.trim()}
                      className="flex-1 px-4 py-3 rounded-lg bg-primary-700 disabled:opacity-40 font-semibold text-base">
                {adding ? 'Qo‘shilmoqda…' : '➕ Vazifa qo‘shish'}
              </button>
            </div>
          </div>

          {/* Queue */}
          <div className="space-y-2">
            <div className="text-sm text-gray-400 px-1">Vazifalar navbati{allMode ? ` — barcha loyihalar (${visibleTasks.length})` : ''}{tasksBusy && !loading ? <span className="ml-1 animate-pulse">⏳</span> : null}</div>
            {loading && <div className="text-gray-500 text-sm px-1">Yuklanmoqda…</div>}
            {!loading && visibleTasks.length === 0 && <div className="text-gray-500 text-sm px-1">Hozircha vazifa yo‘q.</div>}
            {visibleTasks.map(t => (
              <button key={t.id} onClick={() => setOpenId(t.id)}
                      className="w-full text-left bg-gray-800/60 border border-gray-700 rounded-lg p-3 active:bg-gray-800">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <StatusChip status={t.status} approved={!!t.approved_at} answered={!!t.answer} />
                  {canApprove(t) && !t.approved_at && <span className="text-xs text-primary-300">tasdiq kerak →</span>}
                </div>
                {(t.deploy_stage && DEPLOY_M[t.deploy_stage] || t.verify_status) && (
                  <div className="flex flex-wrap gap-1 mb-1">
                    {t.deploy_stage && DEPLOY_M[t.deploy_stage] && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                        ['failed','interrupted','partial'].includes(t.deploy_stage) ? 'bg-red-500/20 text-red-300' : 'bg-blue-500/20 text-blue-300'}`}>
                        {DEPLOY_M[t.deploy_stage]}{t.deploy_detail ? ' · ' + String(t.deploy_detail).slice(0, 28) : ''}
                      </span>
                    )}
                    {t.verify_status && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                        t.verify_status === 'ok' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}>
                        {t.verify_status === 'ok' ? '✅ to‘g‘ri' : '❌ noto‘g‘ri'}{t.verify_by === 'agent' ? ' 🤖' : ''}
                      </span>
                    )}
                  </div>
                )}
                <div className="text-sm line-clamp-2">{t.instruction}</div>
                {Array.isArray(t.sub_tasks) && t.sub_tasks.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {t.sub_tasks.map(st => (
                      <span key={st.id} className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-700/60 text-gray-300 whitespace-nowrap">
                        {st.task_type === 'qa' ? '🧪' : '👨‍💻'} {st.module_name || st.task_type} {SUB_DOT[st.status] || ''}
                      </span>
                    ))}
                  </div>
                )}
                <div className="text-xs text-gray-500 mt-1">#{t.id} · {t.project_name || ''}{Array.isArray(t.sub_tasks) && t.sub_tasks.length > 0 ? ` · ${t.sub_tasks.length} qism` : ''}</div>
              </button>
            ))}
            {tasksMore && !loading && (
              <button onClick={() => fetchTasks({ more: true })} disabled={moreBusy}
                className="w-full py-2.5 rounded-lg border border-gray-700 text-sm text-gray-400 active:bg-gray-800 disabled:opacity-60">
                {moreBusy ? '⏳ Yuklanmoqda…' : '⬇️ Eskilarini yuklash'}
              </button>
            )}
          </div>
        </main>
      )}

      {/* Detail */}
      {openTask && (
        <main className="px-4 py-4 space-y-4 max-w-xl mx-auto">
          <button onClick={() => setOpenId(null)} className="text-gray-400 text-sm">← Orqaga</button>
          <div className="bg-gray-800/60 border border-gray-700 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <StatusChip status={openTask.status} approved={!!openTask.approved_at} answered={!!openTask.answer} />
              <span className="text-xs text-gray-500">#{openTask.id}</span>
            </div>
            <div className="text-sm whitespace-pre-wrap">{openTask.instruction}</div>
            {openTask.answer && (
              <div className="mt-2 rounded-lg border border-sky-500/40 bg-sky-500/10 p-3">
                <div className="text-xs font-semibold text-sky-200 mb-1">💬 Javob</div>
                <div className="text-sm text-gray-100 whitespace-pre-wrap break-words" translate="no">{String(openTask.answer).replace(/^\[JAVOB\]\s*/, '')}</div>
              </div>
            )}
            {Array.isArray(openTask.sub_tasks) && openTask.sub_tasks.length > 0 && (
              <div className="pt-2 space-y-1 border-t border-gray-700">
                <div className="text-[11px] text-gray-500">Qism vazifalar ({openTask.sub_tasks.length}) — to'liq matn uchun bosing:</div>
                {openTask.sub_tasks.map(st => {
                  const open = !!openSubs[st.id];
                  return (
                  <div key={st.id}>
                    <button type="button" onClick={() => toggleSub(st.id)}
                            className="w-full flex items-center justify-between gap-2 text-xs text-left active:opacity-70 py-0.5">
                      <span className="text-gray-300 truncate flex items-center gap-1 min-w-0">
                        <span className="text-gray-500 shrink-0">{open ? '▾' : '▸'}</span>
                        <span className="truncate">{st.task_type === 'qa' ? '🧪' : '👨‍💻'} {st.module_name || st.task_type} — {(st.instruction || '').slice(0, 40)}</span>
                      </span>
                      <StatusChip status={st.status} />
                    </button>
                    {open && (
                      <div className="mt-1 mb-2 ml-4 p-2.5 bg-black/30 border border-gray-700 rounded-lg text-xs text-gray-300 whitespace-pre-wrap break-words">
                        {st.instruction || "(matn yo'q)"}
                        <div className="mt-1.5 pt-1.5 border-t border-gray-700 text-[10px] text-gray-500">
                          #{st.id} · {st.task_type === 'qa' ? '🧪 QA' : '👨‍💻 developer'}{st.module_name ? ` · ${st.module_name}` : ''}
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </div>

          {(openTask.status === 'awaiting_plan_approval' || openTask.plan_ready) && (
            <MobilePlanCard task={openTask} authFetch={authFetch} onChanged={fetchTasks} />
          )}
          {openTask.status === 'awaiting_owner_approval' && (
            <OwnerApprovalCard task={openTask} authFetch={authFetch} apiUrl={API} onChanged={fetchTasks} dark />
          )}

          {/* Clarification — engine quradan oldin savol bermoqda */}
          {openTask.status === 'awaiting_clarification' && (() => {
            const qs = parseQ(openTask);
            return (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
                <div className="text-sm font-semibold text-amber-200 mb-2">❓ Aniqlashtirish kerak — quradan oldin javob bering:</div>
                <div className="space-y-2">
                  {qs.map((q, i) => (
                    <div key={i}>
                      <div className="text-xs text-gray-200 mb-1">{i + 1}. {q}</div>
                      <input
                        value={clarA[openTask.id]?.[i] || ''}
                        onChange={e => setClarA(s => ({ ...s, [openTask.id]: { ...(s[openTask.id] || {}), [i]: e.target.value } }))}
                        placeholder="Javobingiz..."
                        className="w-full text-sm rounded-lg bg-black/30 border border-amber-500/40 px-3 py-2 text-white placeholder-gray-500 focus:outline-none"
                      />
                    </div>
                  ))}
                </div>
                <button onClick={() => sendClar(openTask)} disabled={clarBusy === openTask.id}
                        className="w-full mt-2 px-4 py-2.5 rounded-lg bg-amber-600 disabled:opacity-50 font-semibold text-sm">
                  {clarBusy === openTask.id ? 'Yuborilmoqda…' : '📨 Javob berish'}
                </button>
              </div>
            );
          })()}

          {/* Approve */}
          {canApprove(openTask) && !openTask.approved_at && !isClient && (
            <button onClick={() => approve(openTask.id)} disabled={approvingId === openTask.id}
                    className="w-full px-4 py-3 rounded-lg bg-primary-700 disabled:opacity-50 font-semibold text-base">
              {approvingId === openTask.id ? 'Merge + redeploy…' : '✅ Tasdiqlash (merge + deploy)'}
            </button>
          )}

          {/* Boshqaruv — ish stolidagi ⏳ Jarayonlar bilan bir xil amallar */}
          {!isClient && (() => {
            const t = openTask;
            const active = ['in_progress', 'pending', 'pending_architecture', 'awaiting_plan_approval', 'awaiting_owner_approval'].includes(t.status)
              || (t.sub_tasks || []).some(st => ['in_progress', 'pending'].includes(st.status));
            const canResume = !t.approved_at && (['failed', 'cancelled'].includes(t.status)
              || (t.sub_tasks || []).some(st => ['failed', 'cancelled'].includes(st.status)));
            const canReapprove = ['partial', 'failed', 'interrupted'].includes(t.deploy_stage);
            return (
              <div className="grid grid-cols-2 gap-2">
                {active && (
                  <button onClick={() => { if (window.confirm('Vazifa to‘xtatilsinmi?')) taskAction(t.id, '/stop', null, 'To‘xtatildi'); }}
                          disabled={!!actBusy}
                          className="px-3 py-3 rounded-lg bg-orange-600/80 disabled:opacity-50 font-semibold text-sm">⏹ To‘xtatish</button>
                )}
                {canResume && (
                  <button onClick={() => taskAction(t.id, '/resume', null, 'Davom ettirildi')} disabled={!!actBusy}
                          className="px-3 py-3 rounded-lg bg-blue-600 disabled:opacity-50 font-semibold text-sm">▶ Davom ettirish</button>
                )}
                {canReapprove && (
                  <button onClick={() => taskAction(t.id, '/approve', jsonPost({ force: true }), 'Qayta tasdiqlandi')} disabled={!!actBusy}
                          className="px-3 py-3 rounded-lg bg-primary-700 disabled:opacity-50 font-semibold text-sm">🔁 Qayta tasdiqlash</button>
                )}
                {!active && (
                  <>
                    <button onClick={() => taskAction(t.id, '/verify', jsonPut({ status: t.verify_status === 'ok' ? null : 'ok' }), 'Belgilandi')}
                            disabled={!!actBusy}
                            className={`px-3 py-3 rounded-lg font-semibold text-sm ${t.verify_status === 'ok' ? 'bg-emerald-600' : 'bg-gray-700'}`}>✅ To‘g‘ri</button>
                    <button onClick={() => taskAction(t.id, '/verify', jsonPut({ status: t.verify_status === 'bad' ? null : 'bad' }), 'Belgilandi')}
                            disabled={!!actBusy}
                            className={`px-3 py-3 rounded-lg font-semibold text-sm ${t.verify_status === 'bad' ? 'bg-red-600' : 'bg-gray-700'}`}>❌ Noto‘g‘ri</button>
                    <button onClick={() => taskAction(t.id, '/verify-run', null, 'Agent sinovi boshlandi')} disabled={!!actBusy}
                            className="px-3 py-3 rounded-lg bg-primary-700 disabled:opacity-50 font-semibold text-sm">🧪 Sinash</button>
                    <button onClick={() => { if (window.confirm('Vazifa o‘chirilsinmi? Kod o‘zgarmaydi.')) { taskAction(t.id, '', { method: 'DELETE' }, 'O‘chirildi'); setOpenId(null); } }}
                            disabled={!!actBusy}
                            className="px-3 py-3 rounded-lg bg-gray-700 disabled:opacity-50 font-semibold text-sm">🗑 O‘chirish</button>
                  </>
                )}
              </div>
            );
          })()}

          {/* Media */}
          {(() => {
            const shots = parseUrls(openTask.screenshot_urls);
            const vids = parseUrls(openTask.video_urls);
            const subShots = (openTask.sub_tasks || []).flatMap(st => parseUrls(st.screenshot_urls));
            const subVids = (openTask.sub_tasks || []).flatMap(st => parseUrls(st.video_urls));
            const allShots = [...shots, ...subShots];
            const allVids = [...vids, ...subVids];
            if (!allShots.length && !allVids.length) return null;
            return (
              <div className="space-y-2">
                {allVids.map((u, i) => (
                  <video key={'v' + i} src={mediaUrl(u)} controls playsInline className="w-full rounded-lg border border-gray-700" />
                ))}
                <div className="grid grid-cols-2 gap-2">
                  {allShots.map((u, i) => (
                    <img key={'s' + i} src={mediaUrl(u)} alt="" className="w-full rounded-lg border border-gray-700" />
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Live log */}
          {!isClient && <div className="bg-black/70 border border-gray-800 rounded-lg p-3">
            <div className="text-xs text-gray-500 mb-1">Jonli log</div>
            <div className="font-mono text-xs leading-relaxed max-h-72 overflow-y-auto">
              {openLogs.length === 0 && <div className="text-gray-600">Hali log yo‘q…</div>}
              {openLogs.map((l, i) => (
                <div key={i} className={l.isError ? 'text-red-400 whitespace-pre-wrap' : 'text-gray-300 whitespace-pre-wrap'}>{l.output}</div>
              ))}
              <div ref={logEndRef} />
            </div>
          </div>}
        </main>
      )}

      {/* Emulator FULLSCREEN — fills the phone, touch-interactive */}
      {emuFull != null && (() => {
        const slot = emuFull;
        const frame = emuFrames[slot];
        return (
          <div className="fixed inset-0 z-50 bg-black flex flex-col"
               style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
            <div className="flex items-center justify-between px-3 py-2 gap-2 text-sm bg-gray-900 border-b border-gray-800">
              <span className="font-semibold">📱 Emulator {slot + 1}</span>
              <div className="flex items-center gap-1">
                <button onClick={() => emuKey(slot, 4)}   className="px-2.5 py-1.5 bg-white/10 rounded-lg">←</button>
                <button onClick={() => emuKey(slot, 3)}   className="px-2.5 py-1.5 bg-white/10 rounded-lg">⌂</button>
                <button onClick={() => emuKey(slot, 187)} className="px-2.5 py-1.5 bg-white/10 rounded-lg">▣</button>
                <button onClick={() => emuAppDrawer(slot)} className="px-2.5 py-1.5 bg-white/10 rounded-lg">📲</button>
                <EmuAudioBtn slot={slot} audio={emuAudio} onToggle={toggleEmuAudio} className="px-2.5 py-1.5 rounded-lg" offCls="bg-white/10" onCls="bg-emerald-600" />
                <button onClick={() => { if (emuAudio[slot]) toggleEmuAudio(slot); setEmuFull(null); }} className="px-3 py-1.5 bg-red-600 rounded-lg font-semibold">✕</button>
              </div>
            </div>
            <div className="flex-1 bg-black flex items-center justify-center overflow-hidden select-none"
                 style={{ touchAction: 'none' }}
                 onTouchStart={(e) => emuTouchStart(slot, e)} onTouchMove={(e) => emuTouchMove(slot, e)} onTouchEnd={(e) => emuTouchEnd(slot, e)}>
              {frame
                ? <img src={frame} className="max-w-full max-h-full object-contain pointer-events-none" alt="" draggable={false} />
                : <div className="text-gray-500 text-sm">Yuklanmoqda…</div>}
            </div>
            <input type="text" placeholder="Matn yozib Enter bosing…"
                   className="bg-gray-900 text-gray-100 px-3 py-3 text-base border-t border-gray-800 focus:outline-none"
                   onKeyDown={e => { if (e.key === 'Enter' && e.target.value) { socketRef.current?.emit('emulator:text', { slot, text: e.target.value }); e.target.value = ''; } }} />
          </div>
        );
      })()}
      {/* 🎙 Boshqaruvchi — suzuvchi tugma + to'liq ekran suhbat (ruxsat bo'lmasa chizilmaydi) */}
      {!agentOpen && <Conductor authFetch={authFetch} apiUrl={API} token={token} variant="mobile" />}

      {/* 🧠 Loyiha agenti — to'liq ekran (desktop'dagi bilan bir xil suhbat, xotira, reja, tasdiq kartalari) */}
      {agentOpen && projectId && projectId !== 'all' && (() => {
        const pName = (projects.find(p => String(p.id) === String(projectId)) || {}).name || `#${projectId}`;
        // `dark` TASHQI o'ramda: Tailwind `dark:` faqat ichki elementlarga ta'sir qiladi — ildizning o'zida bo'lsa ildiz foni oq
        // qolib, shaffof reja paneli va sarlavha yozuvi ko'rinmay qolardi (simulyatorda ko'rildi).
        return (
          <div className="dark">
          <div className="kh-m-agent fixed inset-0 z-50 flex flex-col bg-gray-800 text-gray-100"
               style={{ height: '100dvh', paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
            {/* iOS 16px'dan kichik yozuv maydoniga fokusda sahifani kattalashtiradi — agent maydonlari shu oynada 16px */}
            <style>{'.kh-m-agent textarea, .kh-m-agent input, .kh-m-agent select { font-size: 16px; }'}</style>
            <div className="shrink-0 px-2 py-2 flex items-center gap-2 border-b border-gray-700 bg-gray-900">
              <button onClick={closeAgent} aria-label="Orqaga" className="w-10 h-10 shrink-0 rounded-lg text-gray-200 text-xl flex items-center justify-center hover:bg-gray-800">←</button>
              <span className="min-w-0 truncate font-semibold">🧠 Agent · <span translate="no">{pName}</span></span>
            </div>
            <div className="flex-1 min-h-0">
              <OperatorChat authFetch={authFetch} apiUrl={API} token={token} projectId={Number(projectId)} projectName={pName} active={agentOpen} />
            </div>
          </div>
          </div>
        );
      })()}
    </div>
  );
}
